import React from 'react'
import ReactDOM from 'react-dom'
import { colorOptions, screenColorOptions, fabricColorOptions, bottomfabricColorOptions } from './colors'
import { uploadToCloudflare } from './cfUpload'
import { ensureFreshLogin } from './authRenew'
import { track } from './analytics'
import { builderBus } from './builderBus'

const HEROKU = 'https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com'
const FONT_HEADER = "'roc-grotesk-wide', sans-serif"
const FONT_BODY = "'Jost', system-ui, -apple-system, sans-serif"
const COLOR_BLUE = '#6c8ce6'
const COLOR_CREAM = '#faf8f3'
const COLOR_MUTED = '#808082'
const COLOR_BORDER = '#e8e4dc'
const COLOR_BG = '#eeeeef'

// Immediately persist Liquid-injected token to localStorage so getToken() finds it
;(() => {
  try {
    // Pick up Customer Account API token from OAuth callback URL
    const urlParams = new URLSearchParams(window.location.search)
    const caaToken = urlParams.get('caa_token')
    const caaExp = urlParams.get('caa_exp')
    if (caaToken) {
      window.SHOPIFY_CUSTOMER_TOKEN = caaToken
      localStorage.setItem('ohma_ctoken', caaToken)
      localStorage.setItem('ohma_ctoken_exp', caaExp || String(Date.now() + 30 * 24 * 60 * 60 * 1000))
      const caaRefresh = urlParams.get('caa_refresh')
      if (caaRefresh) localStorage.setItem('ohma_ctoken_refresh', caaRefresh)
      // Tell App.js to open My Builds after mount
      try {
        sessionStorage.setItem('ohma_open_builds', '1')
        localStorage.setItem('ohma_open_builds', '1')
      } catch (e) {}
      builderBus.openBuildsOnMount = true
      // Clean URL
      const clean = new URL(window.location.href)
      clean.searchParams.delete('caa_token')
      clean.searchParams.delete('caa_exp')
      window.history.replaceState({}, '', clean.toString())
    }
    // Pick up Liquid-injected token
    const t = window.SHOPIFY_CUSTOMER_TOKEN
    if (t && t !== 'null' && t !== '' && t !== null) {
      localStorage.setItem('ohma_ctoken', t)
      if (!localStorage.getItem('ohma_ctoken_exp')) {
        localStorage.setItem('ohma_ctoken_exp', String(Date.now() + 30 * 24 * 60 * 60 * 1000))
      }
    }
  } catch (e) {}
})()

function getToken() {
  if (window.SHOPIFY_CUSTOMER_TOKEN) return window.SHOPIFY_CUSTOMER_TOKEN
  try {
    const t = localStorage.getItem('ohma_ctoken')
    if (t) {
      window.SHOPIFY_CUSTOMER_TOKEN = t
      return t
    }
  } catch (e) {}
  return null
}
function isTokenExpired() {
  try {
    const exp = localStorage.getItem('ohma_ctoken_exp')
    return exp ? Date.now() > parseInt(exp, 10) : false
  } catch (e) {
    return false
  }
}
// Screen type key -> display label lookup (mirrors App.js screenTypeOptions)
const SCREEN_TYPE_LABELS = { motif: 'Motif', stripes: 'Stripes', windows: 'Windows', scales: 'Scales', holes: 'Holes' }

function isLoggedIn() {
  return !!getToken() && !isTokenExpired()
}
function formatDate(d) {
  const now = new Date()
  const date = new Date(d)
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffSecs < 60) return 'Saved just now'
  if (diffMins < 60) return `Saved ${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `Saved ${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `Saved ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  if (diffWeeks < 4) return `Saved ${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
  if (diffMonths < 12) return `Saved ${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
  return `Saved ${diffYears} year${diffYears !== 1 ? 's' : ''} ago`
}
function resolveColorName(hex, opts) {
  if (!hex || hex === 'none') return null
  if (opts?.[hex]) return opts[hex]
  if (hex === 'Stainless Steel') return 'Stainless (Brushed)'
  if (hex === 'Brass') return 'Brass'
  return hex.startsWith('#') ? hex.toUpperCase() : hex
}
function resolveFabricName(val) {
  if (!val || val === 'none') return null
  return fabricColorOptions?.[val] ?? bottomfabricColorOptions?.[val] ?? val
}

async function apiFetch(path, options = {}) {
  const token = getToken()
  const res = await fetch(`${HEROKU}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-customer-token': token } : {}), ...(options.headers ?? {}) }
  })
  if (!res.ok) {
    const e = await res.json().catch(() => ({}))
    throw new Error(e.error ?? `Request failed (${res.status})`)
  }
  return res.json()
}

function useIsTouch() {
  const [touch, setTouch] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)')
    setTouch(mq.matches)
  }, [])
  return touch
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
      <div
        style={{
          width: 22,
          height: 22,
          border: `2.5px solid ${COLOR_BORDER}`,
          borderTopColor: COLOR_BLUE,
          borderRadius: '50%',
          animation: 'sd-spin 0.7s linear infinite'
        }}
      />
    </div>
  )
}

function AuthForm({ onSuccess }) {
  const [mode, setMode] = React.useState('login')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [recoverSent, setRecoverSent] = React.useState(false)
  const [stayLoggedIn, setStayLoggedIn] = React.useState(true)

  const inp = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${COLOR_BORDER}`,
    borderRadius: 10,
    fontFamily: FONT_BODY,
    fontSize: 16,
    color: '#333',
    background: COLOR_CREAM,
    boxSizing: 'border-box',
    outline: 'none',
    WebkitAppearance: 'none',
    WebkitTextSizeAdjust: '100%'
  }

  // If Liquid already injected a token, use it immediately
  // Only fires migration if token is freshly injected (not already in localStorage)
  React.useEffect(() => {
    const liquidToken = window.SHOPIFY_CUSTOMER_TOKEN
    const storedToken = (() => {
      try {
        return localStorage.getItem('ohma_ctoken')
      } catch (e) {
        return null
      }
    })()
    if (liquidToken && liquidToken !== 'null' && liquidToken !== '') {
      try {
        localStorage.setItem('ohma_ctoken', liquidToken)
        const exp = Date.now() + 30 * 24 * 60 * 60 * 1000
        localStorage.setItem('ohma_ctoken_exp', String(exp))
      } catch (e) {}
      // Only run full migration (handleLoginSuccess) if this is a new token
      // If token was already stored, just set logged in without re-migrating
      if (storedToken && storedToken === liquidToken) {
        // Already knew about this token — skip migration, just mark logged in
        onSuccess()
      } else {
        // Fresh token from Liquid — run full migration path
        onSuccess()
      }
    }
  }, [])

  const submit = async () => {
    setError(null)
    if (mode === 'recover') {
      if (!email) {
        setError('Please enter your email.')
        return
      }
      setLoading(true)
      try {
        await apiFetch('/api/customer/recover', { method: 'POST', body: JSON.stringify({ email }) })
        setRecoverSent(true)
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
      return
    }
    if (!email || !password) {
      setError('Please fill in all fields.')
      return
    }
    if (mode === 'register' && (!firstName || !lastName)) {
      setError('Please fill in all fields.')
      return
    }
    setLoading(true)
    try {
      const body = mode === 'login' ? { email, password } : { email, password, firstName, lastName }
      const data = await apiFetch(`/api/customer/${mode}`, { method: 'POST', body: JSON.stringify(body) })
      window.SHOPIFY_CUSTOMER_TOKEN = data.token
      try {
        localStorage.setItem('ohma_ctoken', data.token)
        // Store expiry — Shopify tokens last ~2 days; if expiresAt not returned, use 47hrs
        const exp = data.expiresAt ? new Date(data.expiresAt).getTime() : Date.now() + 47 * 60 * 60 * 1000
        const finalExp = stayLoggedIn ? Date.now() + 30 * 24 * 60 * 60 * 1000 : exp
        localStorage.setItem('ohma_ctoken_exp', String(finalExp))
      } catch (e) {}
      onSuccess()
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  if (recoverSent)
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <p style={{ margin: '0 0 8px', fontFamily: FONT_HEADER, fontWeight: 700, fontSize: '0.72em', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Check Your Email
        </p>
        <p style={{ margin: '0 0 18px', fontFamily: FONT_BODY, fontSize: 14, color: COLOR_MUTED }}>We sent a reset link to {email}.</p>
        <button
          type="button"
          onClick={() => {
            setMode('login')
            setRecoverSent(false)
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          style={{
            fontFamily: FONT_BODY,
            fontSize: 14,
            color: COLOR_BLUE,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
            transition: 'opacity 0.15s'
          }}>
          Back to Log In
        </button>
      </div>
    )

  return (
    <div style={{ paddingBottom: 8 }}>
      {/* Continue with Shop — opens popup, no page reload */}
      <button
        type="button"
        onClick={() => {
          const authUrl = `https://api.ohmaworld.com/api/auth/start?return_to=${encodeURIComponent('/pages/oauth-complete')}`
          const popup = window.open(authUrl, 'shopify_login', 'width=500,height=700,scrollbars=yes')
          // Listen for token from popup via postMessage
          const handler = (e) => {
            if (e.origin !== 'https://ohmaworld.com') return
            if (e.data?.type !== 'ohma_caa_token') return
            window.removeEventListener('message', handler)
            const { token, exp } = e.data
            window.SHOPIFY_CUSTOMER_TOKEN = token
            try {
              localStorage.setItem('ohma_ctoken', token)
              localStorage.setItem('ohma_ctoken_exp', String(exp))
              if (e.data.refreshToken) localStorage.setItem('ohma_ctoken_refresh', e.data.refreshToken)
            } catch (err) {}
            popup?.close()
            // Trigger re-render by dispatching storage event
            window.dispatchEvent(new Event('ohma_login_complete'))
          }
          window.addEventListener('message', handler)
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: '100%',
          padding: '12px 0',
          marginBottom: 14,
          borderRadius: 10,
          background: '#5A31F4',
          color: '#fff',
          textDecoration: 'none',
          border: 'none',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontWeight: 600,
          fontSize: '15px',
          letterSpacing: '0',
          boxSizing: 'border-box',
          cursor: 'pointer',
          transition: 'background 0.15s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#4C28D4'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#5A31F4'
        }}>
        Sign in with
        <img
          src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/shoplogo.png"
          alt="Shop"
          style={{
            height: '23px',
            width: 'auto',
            marginTop: '2px',
            marginLeft: '-4px',
            filter: 'brightness(0) invert(1)',
            display: 'block'
          }}
        />
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1, height: 1, background: COLOR_BORDER }} />
        <span style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#bbb' }}>or</span>
        <div style={{ flex: 1, height: 1, background: COLOR_BORDER }} />
      </div>
      <button
        type="button"
        onClick={() => {
          setMode('register')
          setError(null)
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = '#e8e6e0')}
        onMouseLeave={(e) => (e.currentTarget.style.background = COLOR_CREAM)}
        style={{
          width: '100%',
          padding: '12px 0',
          marginBottom: 18,
          border: `1px solid ${COLOR_BORDER}`,
          borderRadius: 10,
          background: COLOR_CREAM,
          color: COLOR_MUTED,
          fontFamily: FONT_HEADER,
          fontWeight: 700,
          fontSize: '0.65em',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          transition: 'all 0.15s'
        }}>
        Create Account
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {mode === 'register' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={{ ...inp, flex: 1 }} />
            <input type="text" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} style={{ ...inp, flex: 1 }} />
          </div>
        )}
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={inp} />
        {mode !== 'recover' && (
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            style={inp}
          />
        )}
        {error && <p style={{ margin: 0, color: '#e63946', fontFamily: FONT_BODY, fontSize: 13 }}>{error}</p>}
        {mode !== 'recover' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={stayLoggedIn}
              onChange={(e) => setStayLoggedIn(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: COLOR_BLUE, cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLOR_MUTED }}>Stay logged in for 30 days</span>
          </label>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.background = '#7ab3d0'
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.background = COLOR_BLUE
          }}
          style={{
            width: '100%',
            padding: '11px',
            background: loading ? '#ccc' : COLOR_BLUE,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontFamily: FONT_HEADER,
            fontWeight: 700,
            fontSize: '0.7em',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s'
          }}>
          {loading ? 'Please wait…' : mode === 'login' ? 'Log In' : mode === 'register' ? 'Create Account' : 'Send Reset Link'}
        </button>
      </div>
      <div style={{ textAlign: 'center', marginTop: 18 }}>
        {mode === 'login' && (
          <button
            type="button"
            onClick={() => {
              setMode('recover')
              setError(null)
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            style={{
              fontFamily: FONT_BODY,
              fontSize: 13,
              color: COLOR_MUTED,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              display: 'block',
              margin: '0 auto 6px',
              transition: 'opacity 0.15s'
            }}>
            Forgot your password?
          </button>
        )}
        <p style={{ margin: 0, fontFamily: FONT_BODY, fontSize: 12, color: COLOR_MUTED }}>
          {mode === 'login' ? 'Your Ohma account works across all devices.' : 'Your account also works on ohmaworld.com.'}
        </p>
      </div>
    </div>
  )
}

function DetailSheet({ design, onLoad, onDelete, onDuplicate, onFavorite, onRename, onAddToCart, onClose, screenshotUrl, currentDesignId, hasUnsavedChanges }) {
  // Scroll pm-body to top on mount (grid may have been scrolled)
  React.useEffect(() => {
    const pmBody = document.querySelector('.pm-body-saved-designs')
    if (pmBody) pmBody.scrollTop = 0
    const sheet = document.querySelector('.sd-detail-sheet')
    if (sheet) sheet.scrollTop = 0
    const scroll = document.querySelector('.sd-detail-right-scroll')
    if (scroll) scroll.scrollTop = 0
  }, [])
  const [editingName, setEditingName] = React.useState(false)
  const [nameVal, setNameVal] = React.useState(design.name || 'Untitled Build')
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [hoverBack, setHoverBack] = React.useState(false)
  const [hoverLoad, setHoverLoad] = React.useState(false)
  const [hoverDupe, setHoverDupe] = React.useState(false)
  const [hoverDel, setHoverDel] = React.useState(false)
  const [hoverShare, setHoverShare] = React.useState(false)
  const [hoverFav, setHoverFav] = React.useState(false)
  const [hoverEdit, setHoverEdit] = React.useState(false)
  const [dupeToast, setDupeToast] = React.useState(null)
  const isTouch = useIsTouch()

  // Check if this build is currently loaded and unmodified
  const isEditing = currentDesignId && design.id === currentDesignId && !hasUnsavedChanges

  const cfg = design.config || {}
  const bodyName = resolveColorName(cfg.color, colorOptions)
  const screenName = resolveColorName(cfg.screenColor, screenColorOptions)
  const fabricName = resolveFabricName(cfg.fabricColor)
  const bottomFabricName = resolveFabricName(cfg.bottomFabricColor)

  const handleRename = async () => {
    const t = nameVal.trim() || 'Untitled Build'
    if (t !== (design.name || 'Untitled Build')) await onRename(design.id, t)
    setEditingName(false)
  }
  const handleDelete = async () => {
    setDeleting(true)
    await onDelete(design.id)
    onClose()
  }

  const siteBtn = (extra = {}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '0 14px',
    height: 36,
    border: 'none',
    borderRadius: 10,
    background: '#eeeeef',
    color: '#808082',
    fontFamily: FONT_HEADER,
    fontWeight: 700,
    fontSize: '0.65em',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.15s',
    boxSizing: 'border-box',
    flexShrink: 0,
    ...extra
  })

  const bottomScreenLabel = cfg.bottomScreenType === 'Logo' ? 'Ohma Logo' : cfg.bottomScreenType === 'Custom Logo' ? 'Custom Logo' : 'None'
  const bottomFabricLabel = !cfg.bottomFabricColor || cfg.bottomFabricColor === 'none' ? 'No Fill' : bottomFabricName || cfg.bottomFabricColor

  const rows = [
    { label: 'Body Color', value: bodyName },
    { label: 'Screen Color', value: screenName },
    { label: 'Microphone Type', value: cfg.microphoneType },
    { label: 'Screen Type', value: cfg.screenType ? cfg.screenType.charAt(0).toUpperCase() + cfg.screenType.slice(1) : null },
    { label: 'Fabric Color', value: fabricName },
    { label: 'Logo Fill', value: bottomFabricLabel },
    { label: 'Logo', value: bottomScreenLabel },
    {
      label: 'Pop Filter',
      value: cfg.popFilterEnabled
        ? cfg.popFilterMaterial === 'metal'
          ? (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((cfg.popFilterColor || '').toLowerCase())
              ? 'Black'
              : colorOptions[cfg.popFilterColor] || cfg.popFilterColor || '—') + ' (Metal)'
          : 'Black (Plastic)'
        : null
    },
    {
      label: 'Shock Mount',
      value: cfg.shockMountEnabled ? `Yes · ${resolveColorName(cfg.shockMountColor, colorOptions) || cfg.shockMountColor}` : null
    },
    { label: 'Pad', value: cfg.padEnabled && cfg.microphoneType !== 'Ribbon' ? 'Add Pad' : null }
  ].filter((r) => r.value != null)

  const primaryRows = rows.slice(0, 4)
  const extraRows = rows.slice(4)
  const [showAllDetails, setShowAllDetails] = React.useState(false)
  const [detailsCopied, setDetailsCopied] = React.useState(false)
  const [addingToCart, setAddingToCart] = React.useState(false)
  const [cartDone, setCartDone] = React.useState(false)

  const copyDetails = () => {
    const lines = rows.map((r) => r.label + ': ' + r.value).join('\n')
    navigator.clipboard.writeText(lines).then(() => {
      setDetailsCopied(true)
      setTimeout(() => setDetailsCopied(false), 2000)
    })
  }

  return (
    <div className="sd-detail-sheet">
      <style>{`
        @keyframes sd-spin{to{transform:rotate(360deg)}}
        .sd-detail-input{height:42px !important;font-size:16px !important;box-sizing:border-box !important;-webkit-text-size-adjust:100% !important;}
      `}</style>

      {/* Mobile-only back button — sits above image, hidden on desktop */}
      <button type="button" onClick={onClose} className="sd-detail-back-mobile" style={{ fontFamily: FONT_BODY, color: '#666' }}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
        Back to My Builds
      </button>
      {/* Left — matches ShareContent exactly */}
      <div className="sd-detail-left">
        {/* Favorite star in top left - only show if favorites are enabled */}
        {onFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onFavorite(design.id, !design.is_favorite)
            }}
            onMouseEnter={() => setHoverFav(true)}
            onMouseLeave={() => setHoverFav(false)}
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 14,
              color: design.is_favorite ? '#f0a500' : '#ccc',
              transition: 'all 0.15s',
              boxShadow: 'none',
              zIndex: 10,
              transform: hoverFav && !isTouch ? 'scale(1.15)' : 'scale(1)'
            }}>
            {design.is_favorite ? '★' : '☆'}
          </button>
        )}
        {screenshotUrl ? (
          <img src={screenshotUrl} alt={design.name || 'Build'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : design.thumbnail_url ? (
          <img src={design.thumbnail_url} alt={design.name || 'Build'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <svg
              className="ohma-spinner"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="2.5"
              strokeLinecap="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: FONT_BODY, letterSpacing: '0.05em' }}>Generating preview...</span>
          </div>
        )}
        {/* Download — matches ShareContent */}
        {(screenshotUrl || design.thumbnail_url) && (
          <button
            onClick={() => {
              const u = screenshotUrl || design.thumbnail_url
              const a = document.createElement('a')
              a.href = u
              a.download = 'ohma-build.jpg'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }}
            title="Download"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#555',
              transition: 'background 0.15s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Right — content */}
      <div className="sd-detail-right">
        <div className="sd-detail-right-scroll" style={{ padding: '16px 40px 24px' }}>
          {/* Back button — shown on desktop, hidden on mobile (mobile version is above image) */}
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => !isTouch && setHoverBack(true)}
            onMouseLeave={() => setHoverBack(false)}
            className="sd-detail-back-desktop"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              background: 'transparent',
              border: 'none',
              padding: '0 0 10px',
              cursor: 'pointer',
              color: hoverBack ? '#333' : '#666',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: FONT_BODY,
              flexShrink: 0,
              transition: 'color 0.15s',
              marginLeft: -4
            }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            Back to My Builds
          </button>
          {/* Name */}
          <div style={{ marginBottom: 4 }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                <input
                  className="sd-detail-input"
                  value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                  autoFocus
                  style={{ flex: 1, border: `1px solid ${COLOR_BORDER}`, borderRadius: 8, padding: '0 10px', fontFamily: FONT_BODY, color: '#222' }}
                />
                <button type="button" onClick={handleRename} style={{ ...siteBtn(), padding: '0 10px' }}>
                  Save
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontFamily: `'roc-grotesk-wide', sans-serif`, fontWeight: 700, fontSize: '1.8em', flex: 1 }}>
                  {design.name || 'Untitled Build'}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  onMouseEnter={() => !isTouch && setHoverEdit(true)}
                  onMouseLeave={() => setHoverEdit(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    color: hoverEdit && !isTouch ? '#444' : COLOR_MUTED,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'color 0.15s',
                    borderRadius: 6
                  }}>
                  <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <p style={{ margin: '0 0 14px', fontFamily: FONT_BODY, fontSize: 11, color: COLOR_MUTED }}>
            {formatDate(design.created_at)}
            {design.source === 'cart' && (
              <span
                style={{
                  marginLeft: 6,
                  background: '#E89B88',
                  borderRadius: 12,
                  padding: '4px 10px',
                  fontSize: 11,
                  color: '#fff',
                  fontFamily: FONT_HEADER,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  verticalAlign: 'middle'
                }}>
                In Cart
              </span>
            )}
          </p>

          {/* Add to Cart */}
          {onAddToCart && (
            <button
              type="button"
              className="add-to-cart sd-add-to-cart-btn"
              disabled={addingToCart || cartDone}
              onClick={() => {
                onAddToCart(design)
              }}
              onMouseEnter={(e) => {
                if (!addingToCart && !cartDone) e.currentTarget.style.background = '#4a76e6'
              }}
              onMouseLeave={(e) => {
                if (!addingToCart && !cartDone) e.currentTarget.style.background = '#6186f7'
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: cartDone ? '#5a9a6e' : addingToCart ? '#8aaaf0' : '#6186f7',
                color: '#fff',
                border: 'none',
                borderRadius: '30px',
                cursor: addingToCart ? 'not-allowed' : 'pointer',
                marginBottom: 20,
                boxSizing: 'border-box',
                transition: 'background 0.15s'
              }}>
              {cartDone ? '✓ Added to Cart' : addingToCart ? '…' : 'Add to Cart'}
            </button>
          )}

          {/* Open Build + Duplicate — no icons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => {
                onClose()
                if (onLoad) onLoad(design)
                builderBus.closeFooter?.()
              }}
              onMouseEnter={() => !isTouch && setHoverLoad(true)}
              onMouseLeave={() => setHoverLoad(false)}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: isEditing ? '#fff' : hoverLoad && !isTouch ? '#dcdcdb' : '#eeeeef',
                color: isEditing ? '#e98969' : hoverLoad && !isTouch ? '#333' : '#808082',
                border: isEditing ? '2px solid #e98969' : 'none',
                borderRadius: '30px',
                cursor: 'pointer',
                fontFamily: 'Jost,sans-serif',
                fontWeight: 600,
                fontSize: '13px',
                transition: 'background 0.15s, border 0.15s',
                boxSizing: 'border-box'
              }}>
              {isEditing ? 'SELECTED' : 'Load Build'}
            </button>
            <div style={{ flex: 1, position: 'relative' }}>
              <button
                type="button"
                onClick={() => {
                  const r = onDuplicate(design)
                  if (r === 'limit') {
                    setDupeToast('limit')
                    setTimeout(() => setDupeToast(null), 3000)
                  }
                }}
                onMouseEnter={() => !isTouch && setHoverDupe(true)}
                onMouseLeave={() => setHoverDupe(false)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: hoverDupe && !isTouch ? '#dcdcdb' : '#eeeeef',
                  color: hoverDupe && !isTouch ? '#333' : '#808082',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontFamily: 'Jost,sans-serif',
                  fontWeight: 600,
                  fontSize: '13px',
                  transition: 'background 0.15s',
                  boxSizing: 'border-box'
                }}>
                Duplicate
              </button>
              {dupeToast === 'limit' && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    background: '#000',
                    color: '#fff',
                    fontSize: '12px',
                    fontFamily: 'Jost,sans-serif',
                    fontWeight: 600,
                    padding: '6px 10px',
                    borderRadius: '6px',
                    zIndex: 100,
                    whiteSpace: 'nowrap',
                    letterSpacing: 'normal',
                    textTransform: 'none',
                    lineHeight: 1.4
                  }}>
                  Save limit reached (24 builds max)
                </div>
              )}
            </div>
            {confirmDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '10px 14px',
                  background: '#e63946',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontFamily: 'Jost,sans-serif',
                  fontWeight: 600,
                  fontSize: '13px',
                  flexShrink: 0,
                  opacity: deleting ? 0.7 : 1
                }}>
                {deleting ? '…' : 'Confirm'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                onMouseEnter={() => !isTouch && setHoverDel(true)}
                onMouseLeave={() => setHoverDel(false)}
                style={{
                  width: 36,
                  padding: 0,
                  background: hoverDel && !isTouch ? '#fde8ea' : '#eeeeef',
                  color: hoverDel && !isTouch ? '#e63946' : '#aaa',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            )}
          </div>

          {/* Build Details */}
          <div className="sd-build-details-card">
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #e8e8e6' }}>
              <span
                style={{ flex: 1, fontFamily: FONT_HEADER, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#333' }}>
                Build Details
              </span>
              <button
                onClick={copyDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  color: detailsCopied ? '#5a9a6e' : '#9ca3af',
                  fontSize: 11,
                  fontFamily: FONT_BODY,
                  transition: 'color 0.15s',
                  width: 60,
                  flexShrink: 0,
                  justifyContent: 'flex-end'
                }}>
                {detailsCopied ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            {primaryRows.map((r, i) => (
              <div
                key={i}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid #e8e8e6' }}>
                <span style={{ fontFamily: FONT_HEADER, fontWeight: 600, fontSize: 12, letterSpacing: '0.05em', color: '#1f2937' }}>{r.label}</span>
                <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6b7280' }}>{r.value}</span>
              </div>
            ))}
            {showAllDetails &&
              extraRows.map((r, i) => (
                <div
                  key={i + 4}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '9px 14px',
                    borderBottom: i === extraRows.length - 1 ? 'none' : '1px solid #e8e8e6'
                  }}>
                  <span style={{ fontFamily: FONT_HEADER, fontWeight: 600, fontSize: 12, letterSpacing: '0.05em', color: '#1f2937' }}>{r.label}</span>
                  <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6b7280' }}>{r.value}</span>
                </div>
              ))}
            {extraRows.length > 0 && (
              <button
                onClick={() => setShowAllDetails((v) => !v)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'none',
                  border: 'none',
                  borderTop: '1px solid #e8e8e6',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  color: '#6b7280',
                  textAlign: 'center',
                  transition: 'color 0.15s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#333')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#6b7280')}>
                {showAllDetails ? '− Less' : '+ More Details'}
              </button>
            )}
          </div>

          {confirmDelete && (
            <button type="button" onClick={() => setConfirmDelete(false)} style={{ marginTop: 8, ...siteBtn({ width: '100%' }) }}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function HoverBtn({ onClick, style, hoverStyle, children, className }) {
  const [h, setH] = React.useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      className={className || ''}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{ ...style, ...(h ? hoverStyle : {}), transition: 'all 0.15s', cursor: 'pointer' }}>
      {children}
    </button>
  )
}


function TopBar({
  favOnly,
  onToggleFav,
  onLogout,
  sortBy,
  onSortChange,
  filters,
  onFiltersChange,
  showSortMenu,
  setShowSortMenu,
  showFilterMenu,
  setShowFilterMenu,
  buildCount
}) {
  const sortMenuRef = React.useRef(null)
  const filterMenuRef = React.useRef(null)

  // Close menus when clicking outside
  React.useEffect(() => {
    if (!showSortMenu) return
    const handler = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setShowSortMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showSortMenu])

  React.useEffect(() => {
    if (!showFilterMenu) return
    const handler = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setShowFilterMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showFilterMenu])

  const sortOptions = [
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'az', label: 'A-Z' },
    { value: 'favorites', label: 'Favorites First' }
  ]

  const currentSort = sortOptions.find((o) => o.value === sortBy) || sortOptions[0]

  const btnStyle = {
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 18,
    cursor: 'pointer',
    transition: 'all 0.15s',
    boxSizing: 'border-box',
    border: 'none',
    background: 'transparent',
    fontFamily: FONT_HEADER,
    fontWeight: 600,
    fontSize: '0.72em',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#808082',
    padding: '0 14px'
  }

  const hoverBg = '#eeeeef'

  return (
    <div style={{ marginBottom: 14 }}>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px) scaleY(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scaleY(1);
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: translateY(0) scaleY(1);
          }
          to {
            opacity: 0;
            transform: translateY(-4px) scaleY(0.98);
          }
        }
        .filter-menu-header {
          padding: 10px 12px 6px;
          font-family: 'roc-grotesk-wide', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #999;
        }
        .filter-menu-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          cursor: pointer;
          user-select: none;
          font-family: 'Jost', system-ui, sans-serif;
          font-size: 14px;
          color: #333;
          transition: background 0.15s;
        }
        .filter-menu-option:hover {
          background: #f5f5f5;
        }
        .filter-menu-option input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #91c3de;
          cursor: pointer;
          flex-shrink: 0;
        }
        .filter-menu-option span {
          flex: 1;
        }
        .filter-menu-favorites {
          border-top: 1px solid #e0e0e0;
          margin-top: 4px;
          padding-top: 12px;
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* Star/Favorites Filter */}
        <button
          onClick={onToggleFav}
          style={{
            ...btnStyle,
            width: 36,
            height: 36,
            padding: 0,
            background: favOnly ? '#f0a500' : '#eeeeef',
            color: favOnly ? '#fff' : '#808082'
          }}
          onMouseEnter={(e) => {
            if (!favOnly) {
              e.currentTarget.style.background = '#dcdcdb'
              e.currentTarget.style.color = '#f0a500'
            }
          }}
          onMouseLeave={(e) => {
            if (!favOnly) {
              e.currentTarget.style.background = '#eeeeef'
              e.currentTarget.style.color = '#808082'
            }
          }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={favOnly ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="butt"
            strokeLinejoin="miter"
            style={{ display: 'block' }}>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>

        {/* Divider */}
        <div style={{ width: 1, height: 20, background: '#e0e0e0', margin: '0 2px' }} />

        {/* Filter Button with dropdown */}
        <div style={{ position: 'relative' }} ref={filterMenuRef}>
          <button
            data-filter-button
            onClick={() => {
              setShowSortMenu(false)
              setShowFilterMenu(!showFilterMenu)
            }}
            style={btnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filter
          </button>

          {showFilterMenu && (
            <div
              data-filter-menu
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 10,
                boxShadow: 'none',
                zIndex: 1000,
                minWidth: 200,
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '0',
                animation: 'slideDown 0.15s ease-out',
                transformOrigin: 'top'
              }}>
              {/* Mic Type Section */}
              <div className="filter-menu-header">Mic Type</div>
              {['Condenser', 'Ribbon'].map((type) => (
                <label key={type} className="filter-menu-option">
                  <input
                    type="checkbox"
                    checked={filters.micTypes.includes(type)}
                    onChange={(e) => {
                      const checked = e.target.checked
                      onFiltersChange({
                        ...filters,
                        micTypes: checked ? [...filters.micTypes, type] : filters.micTypes.filter((t) => t !== type)
                      })
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>{type}</span>
                </label>
              ))}

              <div style={{ height: 1, background: '#e0e0e0', margin: '8px 0' }} />

              {/* Screen Type Section */}
              <div className="filter-menu-header">Screen Type</div>
              {[
                { value: 'motif', label: 'Motif' },
                { value: 'holes', label: 'Holes' },
                { value: 'scales', label: 'Scales' },
                { value: 'windows', label: 'Windows' },
                { value: 'stripes', label: 'Stripes' }
              ].map((type) => (
                <label key={type.value} className="filter-menu-option">
                  <input
                    type="checkbox"
                    checked={filters.screenTypes.includes(type.value)}
                    onChange={(e) => {
                      const checked = e.target.checked
                      onFiltersChange({
                        ...filters,
                        screenTypes: checked ? [...filters.screenTypes, type.value] : filters.screenTypes.filter((t) => t !== type.value)
                      })
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>{type.label}</span>
                </label>
              ))}

              <div style={{ height: 1, background: '#e0e0e0', margin: '8px 0' }} />

              {/* Favorites Only */}
              <label className="filter-menu-option filter-menu-favorites">
                <input
                  type="checkbox"
                  checked={filters.favoritesOnly}
                  onChange={(e) => {
                    onFiltersChange({
                      ...filters,
                      favoritesOnly: e.target.checked
                    })
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span>Favorites Only</span>
              </label>
            </div>
          )}
        </div>

        {/* Sort Dropdown */}
        <div style={{ position: 'relative' }} ref={sortMenuRef}>
          <button
            data-sort-button
            onClick={() => {
              setShowFilterMenu(false)
              setShowSortMenu(!showSortMenu)
            }}
            style={btnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
            Sort: {currentSort.label}
          </button>

          {showSortMenu && (
            <div
              data-sort-menu
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 10,
                boxShadow: 'none',
                zIndex: 1000,
                minWidth: 160,
                overflow: 'hidden',
                animation: 'slideDown 0.15s ease-out',
                transformOrigin: 'top'
              }}>
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onSortChange(opt.value)
                    setShowSortMenu(false)
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: 'none',
                    background: sortBy === opt.value ? '#f5f5f5' : '#fff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: FONT_BODY,
                    fontSize: 13,
                    fontWeight: sortBy === opt.value ? 600 : 400,
                    color: sortBy === opt.value ? '#333' : '#555',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                  onMouseLeave={(e) => {
                    if (sortBy !== opt.value) e.currentTarget.style.background = '#fff'
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Build Count - right aligned before logout (desktop only) */}
        {buildCount !== undefined && (
          <div className="sd-desktop-logout" style={{ marginLeft: 'auto', fontFamily: FONT_BODY, fontSize: 13, color: '#e98969', fontWeight: 500 }}>
            {buildCount || 0} / 24
          </div>
        )}

        {/* Logout - pill hover, no default background */}
        <button
          onClick={onLogout}
          style={{ ...btnStyle, marginLeft: buildCount !== undefined ? '24px' : 'auto' }}
          className="sd-desktop-logout"
          onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
          Log out
        </button>
      </div>
    </div>
  )
}

function DesignCard({
  design,
  isActive,
  onInfo,
  onLoad,
  onDelete,
  onDuplicate,
  onFavorite,
  onShare,
  onRename,
  onAddToCart,
  openMenuId,
  setOpenMenuId,
  isLoggedIn,
  onAtLimit
}) {
  const [hoverCard, setHoverCard] = React.useState(false)
  const [hoverLoad, setHoverLoad] = React.useState(false)
  const menuRef = React.useRef(null)
  const [menuPos, setMenuPos] = React.useState(null)
  const [cardDupeToast, setCardDupeToast] = React.useState(false)
  const [cardDupeToastPos, setCardDupeToastPos] = React.useState(null)
  const isTouch = useIsTouch()
  const menuOpen = openMenuId === design.id

  // Debug isActive changes
  React.useEffect(() => {
  }, [isActive, design.id, design.name])

  // Close menu when clicking outside or scrolling
  React.useEffect(() => {
    if (!menuOpen) return
    const handler = (e) => {
      const inButton = menuRef.current && menuRef.current.contains(e.target)
      const inDropdown = e.target.closest('[data-card-menu]')
      if (!inButton && !inDropdown) {
        setOpenMenuId(null)
        setCardDupeToast(false)
        setCardDupeToastPos(null)
      }
    }
    const handleScroll = () => {
      setOpenMenuId(null)
      setCardDupeToast(false)
      setCardDupeToastPos(null)
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [menuOpen, setOpenMenuId])

  React.useEffect(() => {
    if (!cardDupeToast) return
    const dismiss = (e) => {
      if (e.target && e.target.closest && e.target.closest('[data-dupe-toast]')) return
      setCardDupeToast(false)
      setCardDupeToastPos(null)
    }
    const dismissScroll = () => {
      setCardDupeToast(false)
      setCardDupeToastPos(null)
    }
    document.addEventListener('scroll', dismissScroll, true)
    document.addEventListener('mousedown', dismiss)
    return () => {
      document.removeEventListener('scroll', dismissScroll, true)
      document.removeEventListener('mousedown', dismiss)
    }
  }, [cardDupeToast])

  const cfg = design.config || {}

  // Format saved date
  const formatSavedDate = (dateStr) => {
    if (!dateStr) return null
    const now = new Date()
    const saved = new Date(dateStr)
    const diffMs = now - saved
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Saved just now'
    if (diffMins < 60) return `Saved ${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `Saved ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    return `Saved ${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  }

  const subtitle = formatSavedDate(design.updated_at || design.created_at)

  return (
    <div
      style={{
        border: '1px solid #e8e4dc',
        outline: isActive ? '2px solid #e98969' : 'none',
        outlineOffset: isActive ? '1px' : '0',
        borderRadius: 12,
        overflow: 'visible',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'outline 0.2s',
        boxShadow: 'none'
      }}
      onMouseEnter={() => !isTouch && setHoverCard(true)}
      onMouseLeave={() => !isTouch && setHoverCard(false)}>
      {/* Thumbnail with scale effect */}
      <div
        style={{
          width: '100%',
          aspectRatio: '3/4',
          background: '#91c3de',
          overflow: 'hidden',
          position: 'relative',
          cursor: 'pointer',
          borderRadius: '12px 12px 0 0'
        }}
        onClick={() => onInfo(design)}>
        {design.share_thumbnail_url || design.thumbnail_url ? (
          <img
            src={design.share_thumbnail_url || design.thumbnail_url}
            alt={design.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transition: 'transform 0.3s ease',
              transform: hoverCard && !isTouch ? 'scale(1.05)' : 'scale(1)'
            }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}

        {/* Star in top left - only show if favorites are enabled */}
        {onFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onFavorite(design.id, !design.is_favorite)
            }}
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 16,
              color: design.is_favorite ? '#f0a500' : '#ccc',
              transition: 'all 0.15s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)'
              e.currentTarget.style.color = '#f0a500'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
              if (!design.is_favorite) e.currentTarget.style.color = '#ccc'
            }}>
            {design.is_favorite ? '★' : '☆'}
          </button>
        )}

        {/* ... menu button in top right */}
      </div>

      {/* Menu container - button + dropdown */}
      <div ref={menuRef} style={{ position: 'absolute', top: 8, right: 8 }}>
        {/* ... menu button */}
        <button
          data-card-menu-button
          onClick={(e) => {
            e.stopPropagation()
            if (!menuOpen && menuRef.current) {
              const rect = menuRef.current.getBoundingClientRect()
              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
            }
            setOpenMenuId(menuOpen ? null : design.id)
          }}
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.95)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'all 0.15s'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#fff')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.95)')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>
      </div>

      {/* Dropdown menu — rendered via portal into document.body to escape stacking contexts */}
      {menuOpen &&
        menuPos &&
        ReactDOM.createPortal(
          <div
            data-card-menu
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 10,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              zIndex: 999999,
              minWidth: 180,
              overflow: 'hidden',
              animation: 'slideDown 0.15s ease-out',
              transformOrigin: 'top right'
            }}>
            {/* Menu Items */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onInfo(design)
                setOpenMenuId(null)
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: 'none',
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: '#333',
                transition: 'background 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              See Details
            </button>
            {onShare && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onShare(design)
                  setOpenMenuId(null)
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: 'none',
                  background: '#fff',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  color: '#333',
                  transition: 'background 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Share
              </button>
            )}
            {onRename && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRename(design)
                  setOpenMenuId(null)
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: 'none',
                  background: '#fff',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  color: '#333',
                  transition: 'background 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
                Rename
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation()
                const pos = menuPos
                const result = onDuplicate(design)
                setOpenMenuId(null)
                Promise.resolve(result).then((r) => {
                  if (r === 'limit') {
                    if (isLoggedIn) {
                      setCardDupeToastPos(pos)
                      setCardDupeToast(true)
                      setTimeout(() => {
                        setCardDupeToast(false)
                        setCardDupeToastPos(null)
                      }, 3000)
                    } else {
                      onAtLimit && onAtLimit()
                    }
                  }
                })
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: 'none',
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: '#333',
                transition: 'background 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Duplicate
            </button>
            <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0' }} />
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
                setOpenMenuId(null)
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: 'none',
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: '#e63946',
                transition: 'background 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#fff0f0')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
            {onAddToCart && (
              <>
                <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0' }} />
                <div style={{ padding: '4px 8px 12px 8px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      onAddToCart(design)
                      // Don't close menu - let the modal overlay handle closing everything
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      background: '#6c8ce6',
                      textAlign: 'center',
                      cursor: 'pointer',
                      fontFamily: FONT_BODY,
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#fff',
                      transition: 'background 0.15s',
                      borderRadius: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#5a7bd4')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#6c8ce6')}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1" />
                      <circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                    Add to Cart
                  </button>
                </div>
              </>
            )}
          </div>,
          document.body
        )}

      {cardDupeToast &&
        cardDupeToastPos &&
        ReactDOM.createPortal(
          <div
            data-dupe-toast
            style={{
              position: 'fixed',
              top: cardDupeToastPos.top,
              right: cardDupeToastPos.right,
              background: '#000',
              color: '#fff',
              fontSize: '11px',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              padding: '6px 10px',
              borderRadius: '6px',
              zIndex: 999999,
              whiteSpace: 'nowrap',
              letterSpacing: 'normal',
              textTransform: 'none',
              lineHeight: 1.4
            }}>
            Save limit reached (24 builds max)
          </div>,
          document.body
        )}

      {/* Card body with name and subtitle */}
      <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontFamily: FONT_HEADER,
            fontWeight: 600,
            fontSize: '0.8em',
            color: '#333',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
          {design.name || 'Untitled Build'}
        </div>
        {subtitle && (
          <div
            style={{
              fontFamily: FONT_BODY,
              fontSize: '0.7em',
              color: '#999',
              marginBottom: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
            {subtitle}
          </div>
        )}

        {/* Load button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (!isActive) onLoad(design)
          }}
          onMouseEnter={() => !isTouch && setHoverLoad(true)}
          onMouseLeave={() => setHoverLoad(false)}
          style={{
            marginTop: 'auto',
            width: '100%',
            padding: '8px 12px',
            border: isActive ? '1px solid #e98969' : '1px solid #e0e0e0',
            borderRadius: 18,
            background: isActive ? '#fff5f2' : hoverLoad && !isTouch ? '#f5f5f5' : '#fff',
            color: isActive ? '#e98969' : hoverLoad && !isTouch ? '#333' : '#808082',
            cursor: isActive ? 'default' : 'pointer',
            fontFamily: FONT_HEADER,
            fontWeight: 600,
            fontSize: '0.65em',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            transition: 'all 0.15s'
          }}>
          {isActive ? 'SELECTED' : 'Load Build'}
        </button>
      </div>
    </div>
  )
}

function SavePrompt({ onSave, onCancel }) {
  const [name, setName] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [hSave, setHSave] = React.useState(false)
  const [hX, setHX] = React.useState(false)
  const go = async () => {
    setSaving(true)
    setError(null)
    builderBus.setLoading?.('Saving Build...')
    try {
      const result = await onSave(name.trim() || 'My Build')
      if (!result) setError('Save failed — check you are logged in and try again.')
    } catch (e) {
      setError(e.message || 'Save failed')
    } finally {
      setSaving(false)
      builderBus.setLoading?.(null)
    }
  }
  return (
    <div className="sd-save-prompt" style={{ background: '#eeeeef', border: `1px solid ${COLOR_BORDER}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <p
        style={{
          margin: '0 0 10px',
          fontFamily: FONT_HEADER,
          fontWeight: 700,
          fontSize: '0.68em',
          letterSpacing: '0.12em',
          color: '#808082',
          textTransform: 'uppercase'
        }}>
        Name This Build
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="e.g. Studio Setup"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') go()
            if (e.key === 'Escape') onCancel()
          }}
          maxLength={40}
          style={{
            flex: 1,
            height: 42,
            padding: '0 12px',
            border: `1px solid ${COLOR_BORDER}`,
            borderRadius: 10,
            fontFamily: FONT_BODY,
            fontSize: 16,
            background: '#fff',
            outline: 'none',
            boxSizing: 'border-box',
            WebkitAppearance: 'none',
            touchAction: 'manipulation'
          }}
        />
        <button
          type="button"
          onClick={go}
          disabled={saving}
          onMouseEnter={() => setHSave(true)}
          onMouseLeave={() => setHSave(false)}
          style={{
            height: 42,
            padding: '0 16px',
            border: 'none',
            borderRadius: 10,
            background: saving ? '#dcdcdb' : hSave ? '#dcdcdb' : '#fff',
            color: hSave ? '#333' : '#808082',
            fontFamily: FONT_HEADER,
            fontWeight: 700,
            fontSize: '0.65em',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            boxSizing: 'border-box',
            flexShrink: 0,
            transition: 'background 0.15s,color 0.15s'
          }}>
          {saving ? '…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          onMouseEnter={() => setHX(true)}
          onMouseLeave={() => setHX(false)}
          style={{
            height: 42,
            padding: '0 12px',
            border: 'none',
            borderRadius: 10,
            background: hX ? '#dcdcdb' : '#fff',
            color: hX ? '#333' : '#808082',
            fontFamily: FONT_BODY,
            fontSize: 14,
            cursor: 'pointer',
            boxSizing: 'border-box',
            flexShrink: 0,
            transition: 'background 0.15s,color 0.15s'
          }}>
          ✕
        </button>
      </div>
      {error && <p style={{ margin: '8px 0 0', color: '#e63946', fontFamily: FONT_BODY, fontSize: 12 }}>{error}</p>}
    </div>
  )
}

function LoadConfirm({ design, hasUnsaved, currentDesignName, onSaveAndLoad, onDiscard, onCancel }) {
  const [saving, setSaving] = React.useState(false)
  const go = async () => {
    setSaving(true)
    try {
      await onSaveAndLoad()
    } catch (e) {}
    setSaving(false)
  }
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', maxWidth: 320, width: '90%' }}>
        {hasUnsaved ? (
          <>
            <p style={{ margin: '0 0 6px', fontFamily: FONT_HEADER, fontWeight: 700, fontSize: '0.78em', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Unsaved Changes
            </p>
            <p style={{ margin: '0 0 20px', fontFamily: FONT_BODY, color: '#666', fontSize: 14, lineHeight: 1.5 }}>
              "{currentDesignName}" has unsaved changes. Save before loading?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={go}
                disabled={saving}
                style={{
                  padding: '12px',
                  border: 'none',
                  borderRadius: 10,
                  background: COLOR_BLUE,
                  color: '#fff',
                  fontFamily: FONT_HEADER,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}>
                {saving ? 'Saving…' : 'Save & Load'}
              </button>
              <button
                type="button"
                onClick={onDiscard}
                style={{
                  padding: '12px',
                  border: `1px solid ${COLOR_BORDER}`,
                  borderRadius: 10,
                  background: '#fff',
                  color: '#555',
                  fontFamily: FONT_BODY,
                  fontSize: 14,
                  cursor: 'pointer'
                }}>
                Discard & Load
              </button>
              <button
                type="button"
                onClick={onCancel}
                style={{
                  padding: '10px',
                  border: 'none',
                  borderRadius: 10,
                  background: 'none',
                  color: COLOR_MUTED,
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  cursor: 'pointer'
                }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 6px', fontFamily: FONT_HEADER, fontWeight: 700, fontSize: '0.78em', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Load Build
            </p>
            <p style={{ margin: '0 0 20px', fontFamily: FONT_BODY, color: '#666', fontSize: 14 }}>Load "{design?.name || 'Untitled Build'}"?</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: `1px solid ${COLOR_BORDER}`,
                  borderRadius: 10,
                  background: '#fff',
                  color: '#555',
                  fontFamily: FONT_BODY,
                  fontSize: 14,
                  cursor: 'pointer'
                }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={onDiscard}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: 10,
                  background: COLOR_BLUE,
                  color: '#fff',
                  fontFamily: FONT_HEADER,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}>
                Load
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function LocalDetailSheet({ build, onClose, onLoad, onDelete, onRename, onDuplicate, onShare, screenshotUrl, FONT_HEADER, FONT_BODY, COLOR_MUTED }) {
  // Scroll to top on mount
  React.useEffect(() => {
    const pmBody = document.querySelector('.pm-body-saved-designs')
    if (pmBody) pmBody.scrollTop = 0
    const sheet = document.querySelector('.sd-detail-sheet')
    if (sheet) sheet.scrollTop = 0
    const scroll = document.querySelector('.sd-detail-right-scroll')
    if (scroll) scroll.scrollTop = 0
  }, [])
  const [editingName, setEditingName] = React.useState(false)
  const [nameVal, setNameVal] = React.useState(build.name || 'Untitled Build')
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [hoverBack, setHoverBack] = React.useState(false)
  const [hoverLoad, setHoverLoad] = React.useState(false)
  const [hoverDupe, setHoverDupe] = React.useState(false)
  const [hoverShare, setHoverShare] = React.useState(false)
  const [hoverDel, setHoverDel] = React.useState(false)
  const [dupeToast, setDupeToast] = React.useState(null)
  const [hoverEdit, setHoverEdit] = React.useState(false)
  const isTouch = useIsTouch()

  React.useEffect(() => {
    setNameVal(build.name || 'Untitled Build')
  }, [build.name])

  const handleRename = () => {
    const t = nameVal.trim() || 'Untitled Build'
    if (t !== (build.name || 'Untitled Build')) onRename(build.id, t)
    setEditingName(false)
  }


  const cfg = build.config || {}
  const bottomScreenLabelL = cfg.bottomScreenType === 'Logo' ? 'Ohma Logo' : cfg.bottomScreenType === 'Custom Logo' ? 'Custom Logo' : 'None'
  const bottomFabricLabelL =
    !cfg.bottomFabricColor || cfg.bottomFabricColor === 'none' ? 'No Fill' : bottomfabricColorOptions?.[cfg.bottomFabricColor] || cfg.bottomFabricColor

  const rows = [
    { label: 'Body Color', value: cfg.color ? colorOptions[cfg.color] || cfg.color : null },
    { label: 'Screen Color', value: cfg.screenColor ? screenColorOptions[cfg.screenColor] || cfg.screenColor : null },
    { label: 'Microphone Type', value: cfg.microphoneType },
    { label: 'Screen Type', value: cfg.screenType ? cfg.screenType.charAt(0).toUpperCase() + cfg.screenType.slice(1) : null },
    { label: 'Fabric Color', value: cfg.fabricColor && cfg.fabricColor !== 'none' ? fabricColorOptions?.[cfg.fabricColor] || cfg.fabricColor : null },
    { label: 'Logo Fill', value: bottomFabricLabelL },
    { label: 'Logo', value: bottomScreenLabelL },
    {
      label: 'Pop Filter',
      value: cfg.popFilterEnabled
        ? cfg.popFilterMaterial === 'plastic'
          ? 'Black (Plastic)'
          : (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((cfg.popFilterColor || '').toLowerCase())
              ? 'Black'
              : colorOptions[cfg.popFilterColor] || cfg.popFilterColor || '—') + ' (Metal)'
        : null
    },
    {
      label: 'Shock Mount',
      value: cfg.shockMountEnabled ? `Yes · ${colorOptions[cfg.shockMountColor] || cfg.shockMountColor}` : null
    },
    { label: 'Pad', value: cfg.padEnabled && cfg.microphoneType !== 'Ribbon' ? 'Add Pad' : null }
  ].filter((r) => r.value != null)
  const primaryRows = rows.slice(0, 4)
  const extraRows = rows.slice(4)
  const [showAllDetails, setShowAllDetails] = React.useState(false)
  const [detailsCopied, setDetailsCopied] = React.useState(false)

  const copyDetails = () => {
    const lines = rows.map((r) => r.label + ': ' + r.value).join('\n')
    navigator.clipboard.writeText(lines).then(() => {
      setDetailsCopied(true)
      setTimeout(() => setDetailsCopied(false), 2000)
    })
  }

  return (
    <div className="sd-detail-sheet">
      <style>{`@keyframes sd-spin{to{transform:rotate(360deg)}} .sd-detail-input{height:42px !important;font-size:16px !important;box-sizing:border-box !important;-webkit-text-size-adjust:100% !important;}`}</style>

      {/* Mobile-only back button — sits above image, hidden on desktop */}
      <button type="button" onClick={onClose} className="sd-detail-back-mobile" style={{ fontFamily: FONT_BODY, color: '#666' }}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
        Back to My Builds
      </button>
      {/* Left — matches ShareContent exactly */}
      <div className="sd-detail-left">
        {screenshotUrl ? (
          <img src={screenshotUrl} alt={build.name || ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : build.thumbnail_base64 ? (
          <img src={build.thumbnail_base64} alt={build.name || ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : build.thumbnail_url ? (
          <img src={build.thumbnail_url} alt={build.name || ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <svg
              className="ohma-spinner"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="2.5"
              strokeLinecap="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: FONT_BODY, letterSpacing: '0.05em' }}>Generating preview...</span>
          </div>
        )}
        {/* Download — matches ShareContent */}
        {(screenshotUrl || build.thumbnail_url || build.thumbnail_base64) && (
          <button
            onClick={() => {
              const u = screenshotUrl || build.thumbnail_url || build.thumbnail_base64
              const a = document.createElement('a')
              a.href = u
              a.download = 'ohma-build.jpg'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }}
            title="Download"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#555',
              transition: 'background 0.15s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Right — content */}
      <div className="sd-detail-right">
        <div className="sd-detail-right-scroll" style={{ padding: '16px 40px 24px' }}>
          {/* Back button — shown on desktop, hidden on mobile (mobile version is above image) */}
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => !isTouch && setHoverBack(true)}
            onMouseLeave={() => setHoverBack(false)}
            className="sd-detail-back-desktop"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              background: 'transparent',
              border: 'none',
              padding: '0 0 10px',
              cursor: 'pointer',
              color: hoverBack ? '#333' : '#666',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: FONT_BODY,
              flexShrink: 0,
              transition: 'color 0.15s',
              marginLeft: -4
            }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            Back to My Builds
          </button>
          {/* Name */}
          <div style={{ marginBottom: 4 }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                <input
                  className="sd-detail-input"
                  value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                  autoFocus
                  style={{ flex: 1, border: `1px solid ${COLOR_BORDER}`, borderRadius: 8, padding: '0 10px', fontFamily: FONT_BODY, color: '#222' }}
                />
                <button
                  type="button"
                  onClick={handleRename}
                  style={{
                    padding: '0 10px',
                    height: 36,
                    border: 'none',
                    borderRadius: 8,
                    background: '#eeeeef',
                    color: '#808082',
                    fontFamily: FONT_HEADER,
                    fontWeight: 700,
                    fontSize: '0.65em',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}>
                  Save
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontFamily: `'roc-grotesk-wide', sans-serif`, fontWeight: 700, fontSize: '1.8em', flex: 1 }}>
                  {build.name || 'Untitled Build'}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  onMouseEnter={() => !isTouch && setHoverEdit(true)}
                  onMouseLeave={() => setHoverEdit(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    color: hoverEdit && !isTouch ? '#444' : COLOR_MUTED,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'color 0.15s',
                    borderRadius: 6
                  }}>
                  <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Sign up CTA */}
          <p style={{ margin: '0 0 16px', fontFamily: FONT_BODY, fontSize: 12, color: COLOR_MUTED, lineHeight: 1.5 }}>
            Save this build and more by{' '}
            <span
              onClick={() => {
                onClose()
                setTimeout(() => {
                  const el = document.getElementById('sd-auth-form')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }, 100)
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}>
              signing in
            </span>
            .
          </p>

          {/* Add to Cart + Load Build + Duplicate — no icons */}
          <button
            type="button"
            className="sd-add-to-cart-btn"
            onClick={() => {
              if (typeof builderBus.openConfirmForDesign === 'function') builderBus.openConfirmForDesign({ ...build, config: build.config })
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#4a76e6')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#6186f7')}
            style={{
              width: '100%',
              padding: '12px 20px',
              background: '#6186f7',
              color: '#fff',
              border: 'none',
              borderRadius: '30px',
              cursor: 'pointer',
              marginBottom: 12,
              boxSizing: 'border-box',
              transition: 'background 0.15s'
            }}>
            Add to Cart
          </button>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <button
              type="button"
              onClick={() => {
                onClose()
                if (onLoad) onLoad(build.config)
                builderBus.closeFooter?.()
              }}
              onMouseEnter={() => !isTouch && setHoverLoad(true)}
              onMouseLeave={() => setHoverLoad(false)}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: hoverLoad && !isTouch ? '#dcdcdb' : '#eeeeef',
                color: hoverLoad && !isTouch ? '#333' : '#808082',
                border: 'none',
                borderRadius: '30px',
                cursor: 'pointer',
                fontFamily: 'Jost,sans-serif',
                fontWeight: 600,
                fontSize: '13px',
                transition: 'background 0.15s',
                boxSizing: 'border-box'
              }}>
              Load Build
            </button>
            <div style={{ flex: 1, position: 'relative' }}>
              <button
                type="button"
                onClick={() => {
                  const r = onDuplicate(build)
                  if (r === 'limit') {
                    setDupeToast('limit')
                    setTimeout(() => setDupeToast(null), 3000)
                  }
                }}
                onMouseEnter={() => !isTouch && setHoverDupe(true)}
                onMouseLeave={() => setHoverDupe(false)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: hoverDupe && !isTouch ? '#dcdcdb' : '#eeeeef',
                  color: hoverDupe && !isTouch ? '#333' : '#808082',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontFamily: 'Jost,sans-serif',
                  fontWeight: 600,
                  fontSize: '13px',
                  transition: 'background 0.15s',
                  boxSizing: 'border-box'
                }}>
                Duplicate
              </button>
              {dupeToast === 'limit' && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    background: '#000',
                    color: '#fff',
                    fontSize: '12px',
                    fontFamily: 'Jost,sans-serif',
                    fontWeight: 600,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    zIndex: 100,
                    whiteSpace: 'nowrap',
                    letterSpacing: 'normal',
                    textTransform: 'none',
                    lineHeight: 1.4
                  }}>
                  Save this build and more by{' '}
                  <span
                    onClick={() => {
                      onClose()
                      setTimeout(() => {
                        const el = document.getElementById('sd-auth-form')
                        if (el) el.scrollIntoView({ behavior: 'smooth' })
                      }, 100)
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    signing in
                  </span>
                  .
                </div>
              )}
            </div>
            {confirmDelete ? (
              <button
                type="button"
                onClick={() => {
                  onDelete(build.id)
                  onClose()
                }}
                style={{
                  padding: '10px 14px',
                  background: '#e63946',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  fontFamily: 'Jost,sans-serif',
                  fontWeight: 600,
                  fontSize: '13px',
                  flexShrink: 0
                }}>
                Confirm
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                onMouseEnter={() => !isTouch && setHoverDel(true)}
                onMouseLeave={() => setHoverDel(false)}
                style={{
                  width: 40,
                  padding: 0,
                  background: hoverDel && !isTouch ? '#fde8ea' : '#eeeeef',
                  color: hoverDel && !isTouch ? '#e63946' : '#aaa',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            )}
          </div>

          {/* Build Details — share page style */}
          <div className="sd-build-details-card">
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #e8e8e6' }}>
              <span
                style={{ flex: 1, fontFamily: FONT_HEADER, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#333' }}>
                Build Details
              </span>
              <button
                onClick={copyDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  color: detailsCopied ? '#5a9a6e' : '#9ca3af',
                  fontSize: 11,
                  fontFamily: FONT_BODY,
                  transition: 'color 0.15s',
                  width: 60,
                  flexShrink: 0,
                  justifyContent: 'flex-end'
                }}>
                {detailsCopied ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            {primaryRows.map((r, i) => (
              <div
                key={i}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid #e8e8e6' }}>
                <span style={{ fontFamily: FONT_HEADER, fontWeight: 600, fontSize: 12, letterSpacing: '0.05em', color: '#1f2937' }}>{r.label}</span>
                <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6b7280' }}>{r.value}</span>
              </div>
            ))}
            {showAllDetails &&
              extraRows.map((r, i) => (
                <div
                  key={i + 4}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '9px 14px',
                    borderBottom: i === extraRows.length - 1 ? 'none' : '1px solid #e8e8e6'
                  }}>
                  <span style={{ fontFamily: FONT_HEADER, fontWeight: 600, fontSize: 12, letterSpacing: '0.05em', color: '#1f2937' }}>{r.label}</span>
                  <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6b7280' }}>{r.value}</span>
                </div>
              ))}
            {extraRows.length > 0 && (
              <button
                onClick={() => setShowAllDetails((v) => !v)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'none',
                  border: 'none',
                  borderTop: '1px solid #e8e8e6',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  color: '#6b7280',
                  textAlign: 'center',
                  transition: 'color 0.15s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#333')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#6b7280')}>
                {showAllDetails ? '− Less' : '+ More Details'}
              </button>
            )}
          </div>

          {confirmDelete && (
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              style={{
                marginTop: 8,
                width: '100%',
                padding: '0 14px',
                height: 36,
                border: 'none',
                borderRadius: 10,
                background: '#eeeeef',
                color: '#808082',
                fontFamily: FONT_HEADER,
                fontWeight: 700,
                fontSize: '0.65em',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Module-level cache of share_thumbnail_urls — survives component unmount/remount
// Keyed by design ID, populated during migration, save, and fetchDesigns
const _knownShareUrls = {}

export default function SavedDesignsTab({
  config,
  onLoadDesign,
  onSaveDesign,
  onUpdateDesign,
  onAddToCart,
  currentDesignId,
  currentDesignName,
  hasUnsavedChanges,
  shareScreenshotUrl,
  onRegenerateScreenshot,
  onDetailChange,
  onScrollReset,
  onCartAdded,
  onMigratedId
}) {
  const [loggedIn, setLoggedIn] = React.useState(isLoggedIn)
  const [loginLoading, setLoginLoading] = React.useState(false)
  const [designs, setDesigns] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [showSaveForm, setShowSaveForm] = React.useState(false)
  const [pendingAddToCart, setPendingAddToCart] = React.useState(null)
  // Persists known share_thumbnail_urls across fetchDesigns calls — see module-level _knownShareUrls
  const [detailDesign, setDetailDesignRaw] = React.useState(null)

  // Debug currentDesignId changes
  React.useEffect(() => {
  }, [currentDesignId, currentDesignName, hasUnsavedChanges])
  const setDetailDesign = (v) => {
    // Close all menus when navigating to/from detail view
    setOpenMenuId(null)
    setShowFilterMenu(false)
    setShowSortMenu(false)

    setDetailDesignRaw(v)
    if (onDetailChange) onDetailChange(!!v)
    if (onScrollReset) onScrollReset()
    // Reset scroll in both directions — opening detail OR going back to grid
    requestAnimationFrame(() => {
      const pmBody = document.querySelector('.pm-body-saved-designs')
      if (pmBody) pmBody.scrollTop = 0
      const sheet = document.querySelector('.sd-detail-sheet')
      if (sheet) sheet.scrollTop = 0
      const scroll = document.querySelector('.sd-detail-right-scroll')
      if (scroll) scroll.scrollTop = 0
    })
  }

  // Allow external reset/open (e.g. from BuildsDrawer)
  React.useEffect(() => {
    builderBus.resetBuildsDetail = () => setDetailDesign(null)
    builderBus.openBuildsDetail = (design) => setDetailDesign(design)
    builderBus.doAddToCart = (design) => doAddToCart(design)
    return () => {
      builderBus.resetBuildsDetail = null
      builderBus.openBuildsDetail = null
      builderBus.doAddToCart = null
    }
  }, [])
  const [confirmLoad, setConfirmLoad] = React.useState(null)

  // Poll for Liquid-injected token — it may arrive after React mounts
  React.useEffect(() => {
    if (loggedIn) return // already logged in, skip
    let attempts = 0
    const iv = setInterval(() => {
      attempts++
      const token = getToken()
      if (token) {
        setLoggedIn(true)
        clearInterval(iv)
      } else if (attempts >= 10) {
        clearInterval(iv) // give up after ~5 seconds
      }
    }, 500)
    return () => clearInterval(iv)
  }, [])

  // Listen for popup OAuth completion — no page reload needed
  React.useEffect(() => {
    const handler = () => {
      const token = getToken()
      if (token) handleLoginSuccess()
    }
    window.addEventListener('ohma_login_complete', handler)
    return () => window.removeEventListener('ohma_login_complete', handler)
  }, [])
  const [favOnly, setFavOnly] = React.useState(false)
  const [sortBy, setSortBy] = React.useState('newest')
  const [filters, setFilters] = React.useState({
    micTypes: [], // 'Condenser', 'Ribbon'
    screenTypes: [], // 'motif', 'holes', 'scales', 'windows', 'stripes'
    favoritesOnly: false
  })
  const [renameModalDesign, setRenameModalDesign] = React.useState(null)
  const [renameInputValue, setRenameInputValue] = React.useState('')
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null)
  const [deleting, setDeleting] = React.useState(false)
  const [openMenuId, setOpenMenuId] = React.useState(null)
  const [showFilterMenu, setShowFilterMenu] = React.useState(false)
  const [showSortMenu, setShowSortMenu] = React.useState(false)

  // Close all menus when component unmounts (modal closes)
  React.useEffect(() => {
    return () => {
      setOpenMenuId(null)
      setShowFilterMenu(false)
      setShowSortMenu(false)
    }
  }, [])

  // Global click-outside handler to close all menus
  React.useEffect(() => {
    if (!openMenuId && !showFilterMenu && !showSortMenu) return

    const handleClickOutside = (e) => {
      // Check if click is inside any menu or button
      const target = e.target

      // Check if clicking on filter/sort buttons or their menus
      const filterButton = target.closest('[data-filter-button]')
      const filterMenu = target.closest('[data-filter-menu]')
      const sortButton = target.closest('[data-sort-button]')
      const sortMenu = target.closest('[data-sort-menu]')
      const cardMenu = target.closest('[data-card-menu]')
      const cardMenuButton = target.closest('[data-card-menu-button]')

      if (!filterButton && !filterMenu && !sortButton && !sortMenu && !cardMenu && !cardMenuButton) {
        // Click is outside all menus - close everything
        setOpenMenuId(null)
        setShowFilterMenu(false)
        setShowSortMenu(false)
      }
    }

    // Use capture phase to catch events before stopPropagation
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [openMenuId, showFilterMenu, showSortMenu])

  // Close card menus when filter/sort opens, and vice versa
  React.useEffect(() => {
    if (showFilterMenu || showSortMenu) {
      setOpenMenuId(null)
    }
  }, [showFilterMenu, showSortMenu])

  React.useEffect(() => {
    if (openMenuId) {
      setShowFilterMenu(false)
      setShowSortMenu(false)
    }
  }, [openMenuId])

  const fetchDesigns = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const d = await apiFetch('/api/designs')
      let designs = d.designs ?? []

      // Check actual Shopify cart — clear 'in cart' badge for builds no longer in cart
      try {
        const cartUrl = window.location.hostname.includes('customize.') ? 'https://ohmaworld.com/cart.js' : '/cart.js'
        const cartRes = await fetch(cartUrl, { credentials: 'include' })
        if (cartRes.ok) {
          const cart = await cartRes.json()
          const cartFileids = new Set((cart.items ?? []).map((item) => String(item.properties?.['_fileid'] ?? item.properties?.fileid ?? '')).filter(Boolean))
          const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000
          const staleCartIds = designs
            .filter((d) => d.source === 'cart')
            .filter((d) => {
              if (!cart.items?.length) return true
              const tooOld = d.updated_at && new Date(d.updated_at).getTime() < fourteenDaysAgo
              if (tooOld) return true
              return !cart.items.some((item) => {
                const fid = item.properties?.['_fileid'] || item.properties?.fileid
                return fid && cartFileids.has(String(fid))
              })
            })
            .map((d) => d.id)

          if (staleCartIds.length > 0) {
            // Clear source on stale cart builds
            await Promise.all(staleCartIds.map((id) => apiFetch(`/api/designs/${id}/cart-status`, { method: 'DELETE' }).catch(() => {})))
            designs = designs.map((d) => (staleCartIds.includes(d.id) ? { ...d, source: null } : d))
          }
        }
      } catch (e) {} // cart check is non-fatal

      setDesigns((prev) => {
        // Merge: prefer server value if it has share_thumbnail_url,
        // otherwise fall back to known URLs ref, then existing state
        return designs.map((d) => {
          const knownUrl = _knownShareUrls[d.id]
          const existing = prev.find((p) => p.id === d.id)
          const shareUrl = d.share_thumbnail_url || knownUrl || existing?.share_thumbnail_url || null
          // Store in ref for future fetches
          if (shareUrl) _knownShareUrls[d.id] = shareUrl
          return shareUrl ? { ...d, share_thumbnail_url: shareUrl } : d
        })
      })
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Expose fetchDesigns globally so App.js can trigger a refresh after save
  React.useEffect(() => {
    builderBus.refreshBuilds = fetchDesigns
    // If a save happened before this tab was opened, prepend the design immediately
    // then do a delayed fetch to pick up the thumbnail once it's uploaded
    if (builderBus.pendingDesign) {
      const pending = builderBus.pendingDesign
      builderBus.pendingDesign = null
      builderBus.pendingRefresh = false
      setDesigns((prev) => {
        if (prev.some((d) => d.id === pending.id)) return prev
        return [pending, ...prev]
      })
      // Always fetch after 3s to get thumbnail URL once upload completes
      setTimeout(() => fetchDesigns(), 3000)
    } else if (builderBus.pendingRefresh) {
      builderBus.pendingRefresh = false
      fetchDesigns()
    }
    // Recover pending thumbnail from localStorage if user navigated away before upload
    ;(async () => {
      try {
        const designId = localStorage.getItem('ohma_pending_thumb_design_id')
        const thumbKey = localStorage.getItem('ohma_pending_thumb_key')
        const base64 = thumbKey ? localStorage.getItem(thumbKey) : null
        const token = window.SHOPIFY_CUSTOMER_TOKEN || localStorage.getItem('ohma_ctoken') || ''
        if (!designId || !base64 || !token) return
        // Convert base64 data URL to blob without fetch() — more reliable on mobile
        const mime = base64.match(/data:([^;]+)/)?.[1] || 'image/jpeg'
        const binaryStr = atob(base64.split(',')[1])
        const bytes = new Uint8Array(binaryStr.length)
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i)
        const blob = new Blob([bytes], { type: mime })
        // Failed: keep the pending copy so the next visit to My Builds tries again
        const imageUrl = await uploadToCloudflare(blob, `build_${designId}.jpg`)
        if (!imageUrl) return
        await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${designId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
          body: JSON.stringify({ thumbnail_url: imageUrl })
        })
        localStorage.removeItem(thumbKey)
        localStorage.removeItem('ohma_pending_thumb_key')
        localStorage.removeItem('ohma_pending_thumb_design_id')
        fetchDesigns()
      } catch (e) {}
    })()
    return () => {
      builderBus.refreshBuilds = null
    }
  }, [fetchDesigns])

  // Reset detail view when component remounts (tab leave/return goes to list)
  React.useEffect(() => {
    setDetailDesign(null)
  }, [])

  // Auto-renew token using refresh token — fires on mount, visibility change, and every 5 min
  React.useEffect(() => {
    // Shared renewal (src/authRenew.js): one at a time for the whole page
    const tryRefresh = async () => {
      if (!localStorage.getItem('ohma_ctoken_refresh')) return // No refresh token — can't refresh
      const ok = await ensureFreshLogin()
      if (ok) {
        window.SHOPIFY_CUSTOMER_TOKEN = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN
        setLoggedIn(true)
      } else if (!localStorage.getItem('ohma_ctoken')) {
        // Refresh token itself has expired — need to log in again
        window.SHOPIFY_CUSTOMER_TOKEN = null
        setLoggedIn(false)
      }
    }
    // Fire immediately on mount
    tryRefresh()
    // Fire every 5 minutes (catches tokens about to expire while app is open)
    const iv = setInterval(tryRefresh, 5 * 60 * 1000)
    // Fire when user comes back to the tab — catches tokens that expired while backgrounded
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryRefresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(iv)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // Trigger screenshot regeneration on mount — same pattern as share tab
  React.useEffect(() => {
    if (onRegenerateScreenshot) onRegenerateScreenshot()
  }, [])

  React.useEffect(() => {
    if (!loggedIn) return
    fetchDesigns()
    const iv = setInterval(fetchDesigns, 30000)
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchDesigns()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      clearInterval(iv)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [loggedIn, fetchDesigns])

  const saveNew = async (name) => {
    const design = await onSaveDesign?.(name)
    if (design) {
      setDesigns((prev) => [design, ...prev])
      setShowSaveForm(false)
    } else {
      throw new Error('Save failed — make sure you are logged in.')
    }
  }

  const saveCurrent = async () => {
    if (!currentDesignId) return
    builderBus.setLoading?.('Saving Build...')
    // Optimistically mark as saving to show spinner on card
    setDesigns((prev) => prev.map((d) => (d.id === currentDesignId ? { ...d, config, updated_at: new Date().toISOString(), _saving: true } : d)))
    const thumbnailUrl = await onUpdateDesign?.(currentDesignId, { config })
    // Immediately update card with new thumbnail if we got one
    setDesigns((prev) => prev.map((d) => (d.id === currentDesignId ? { ...d, _saving: false, ...(thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {}) } : d)))
    builderBus.setLoading?.(null)
  }

  // Allow App.js to patch share_thumbnail_url into a design after async upload
  React.useEffect(() => {
    builderBus.patchDesignShareUrl = ({ id, share_thumbnail_url }) => {
      if (id && share_thumbnail_url) _knownShareUrls[id] = share_thumbnail_url
      setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, share_thumbnail_url } : d)))
      setDetailDesignRaw((prev) => (prev?.id === id ? { ...prev, share_thumbnail_url } : prev))
    }
    return () => {
      builderBus.patchDesignShareUrl = null
    }
  }, [])

  // Expose saveCurrent globally so canvas save icon in App.js can call it
  React.useEffect(() => {
    builderBus.saveCurrent = saveCurrent
    return () => {
      builderBus.saveCurrent = null
    }
  })
  const doDelete = async (id) => {
    try {
      await apiFetch(`/api/designs/${id}`, { method: 'DELETE' })
      setDesigns((prev) => prev.filter((d) => d.id !== id))
    } catch (e) {
      console.error('Delete failed:', e)
      setError('Failed to delete. Please try again.')
    }
  }
  const doFavorite = async (id, val) => {
    await apiFetch(`/api/designs/${id}/favorite`, { method: 'PATCH', body: JSON.stringify({ is_favorite: val }) })
    setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, is_favorite: val } : d)))
    if (detailDesign?.id === id) setDetailDesign((prev) => ({ ...prev, is_favorite: val }))
  }
  const doRename = async (id, name) => {
    await apiFetch(`/api/designs/${id}`, { method: 'PUT', body: JSON.stringify({ name }) })
    setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)))
    if (detailDesign?.id === id) setDetailDesign((prev) => ({ ...prev, name }))
  }

  const executeRename = async () => {
    if (!renameModalDesign || !renameInputValue.trim()) return
    if (renameModalDesign.source === 'local') {
      // Local build rename
      try {
        const builds = JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
        const updated = builds.map((b) => (b.id === renameModalDesign.id ? { ...b, name: renameInputValue.trim() } : b))
        localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(updated))
        setLocalBuilds(updated)
      } catch (e) {}
    } else {
      // Server build rename
      await doRename(renameModalDesign.id, renameInputValue.trim())
    }
    setRenameModalDesign(null)
    setRenameInputValue('')
  }

  const executeDelete = async (design) => {
    setDeleting(true)
    try {
      await doDelete(design.id)
      setConfirmDeleteId(null)
    } catch (e) {
      console.error('Delete failed:', e)
    }
    setDeleting(false)
  }

  const doLocalShare = async (build) => {
    const cfg = build.config || {}
    const colorLabel = colorOptions[cfg.color] || cfg.color || '—'
    const screenTypeLabel = SCREEN_TYPE_LABELS[cfg.screenType] || cfg.screenType || '—'
    const screenColorLabel = screenColorOptions[cfg.screenColor] || cfg.screenColor || '—'
    const fabricColorLabel = fabricColorOptions[cfg.fabricColor] || cfg.fabricColor || '—'
    const micLabel = cfg.microphoneType || '—'
    const popLabel = cfg.popFilterEnabled
      ? cfg.popFilterMaterial === 'plastic'
        ? 'Black (Plastic)'
        : (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((cfg.popFilterColor || '').toLowerCase())
            ? 'Black'
            : colorOptions[cfg.popFilterColor] || cfg.popFilterColor || '—') + ' (Metal)'
      : null

    const buildLines = [
      colorLabel ? `Body: ${colorLabel}` : null,
      screenTypeLabel ? `Screen Type: ${screenTypeLabel}` : null,
      screenColorLabel ? `Screen Color: ${screenColorLabel}` : null,
      fabricColorLabel ? `Fabric: ${fabricColorLabel}` : null,
      micLabel ? `Mic Type: ${micLabel}` : null,
      popLabel ? `Pop Filter: ${popLabel}` : null
    ]
      .filter(Boolean)
      .join('\n')
    const cfg2 = build.config || {}
    // Always generate a fresh fileid and POST config to Heroku so the share URL loads the build
    const localFileid = Date.now()
    fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...cfg2, fileid: localFileid })
    }).catch(() => {})
    const shareUrl = `https://ohmaworld.com/products/build-your-ohma/?id=${localFileid}`
    const bottomFabricLabel =
      !cfg.bottomFabricColor || cfg.bottomFabricColor === 'none' ? null : bottomfabricColorOptions[cfg.bottomFabricColor] || cfg.bottomFabricColor
    const bottomScreenLabel = cfg.bottomScreenType === 'Logo' ? 'Ohma Logo' : cfg.bottomScreenType === 'Custom Logo' ? 'Custom Logo' : null
    const shareText = [
      'Check out my custom Ohma mic build:',
      shareUrl,
      '',
      micLabel ? `Microphone Type: ${micLabel}` : null,
      colorLabel ? `Body Color: ${colorLabel}` : null,
      screenTypeLabel ? `Screen Type: ${screenTypeLabel}` : null,
      screenColorLabel ? `Screen Color: ${screenColorLabel}` : null,
      fabricColorLabel ? `Fabric Color: ${fabricColorLabel}` : null,
      bottomFabricLabel ? `Logo Fill: ${bottomFabricLabel}` : null,
      bottomScreenLabel ? `Logo: ${bottomScreenLabel}` : null,
      popLabel ? `Pop Filter: ${popLabel}` : null
    ]
      .filter((l) => l !== null)
      .join('\n')

    // Prefer high-res portrait share thumbnail, fall back to square thumbnail
    const shareThumbnail = build.share_thumbnail_base64 || build.share_thumbnail_url || build.thumbnail_base64 || build.thumbnail_url
    if (shareThumbnail && navigator.share && navigator.canShare) {
      try {
        let blob
        if (shareThumbnail.startsWith('data:')) {
          const res = await fetch(shareThumbnail)
          blob = await res.blob()
        } else {
          const res = await fetch(shareThumbnail)
          blob = await res.blob()
        }
        const file = new File([blob], 'my-ohma-mic-build.png', { type: 'image/png' })
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ text: shareText, files: [file] })
          return
        }
      } catch (e) {
        if (e.name === 'AbortError') return
      }
    }
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText })
      } catch (e) {}
    } else {
      try {
        await navigator.clipboard.writeText(shareText)
      } catch (e) {}
    }
  }

  const doShare = async (design) => {
    const cfg = design.config || {}
    const props = cfg.cartProperties || {}
    const isLocal = design.source === 'local' || !design.share_thumbnail_url
    // Always generate a fresh fileid and POST config to Heroku so the share URL loads the build
    const fileid = Date.now()
    fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...cfg, fileid })
    }).catch(() => {})
    const shareUrl = `https://ohmaworld.com/products/build-your-ohma/?id=${fileid}`

    // Build labels — check cartProperties first, then stored labels, then lookup from options
    const colorLabel = props['Body Color'] || cfg.colorLabel || colorOptions[cfg.color] || cfg.color || '—'
    const screenTypeLabel = props['Screen Type'] || SCREEN_TYPE_LABELS[cfg.screenType] || cfg.screenTypeLabel || cfg.screenType || '—'
    const screenColorLabel = props['Screen Color'] || cfg.screenColorLabel || screenColorOptions[cfg.screenColor] || cfg.screenColor || '—'
    const fabricColorLabel = props['Fabric Color'] || cfg.fabricColorLabel || fabricColorOptions[cfg.fabricColor] || cfg.fabricColor || '—'
    const bottomFabricLabel =
      props['Bottom Fabric Color'] || cfg.bottomFabricColorLabel || bottomfabricColorOptions[cfg.bottomFabricColor] || cfg.bottomFabricColor || '—'
    const bottomScreenLabel = props['Bottom Screen Type'] || cfg.bottomScreenTypeLabel || cfg.bottomScreenType || '—'
    const micLabel = props['Microphone Type'] || cfg.microphoneTypeLabel || cfg.microphoneType || '—'
    const popLabel = cfg.popFilterEnabled
      ? props['Pop Filter Color'] ||
        (cfg.popFilterMaterial === 'plastic'
          ? 'Black (Plastic)'
          : (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((cfg.popFilterColor || '').toLowerCase())
              ? 'Black'
              : colorOptions[cfg.popFilterColor] || cfg.popFilterColor || '—') + ' (Metal)')
      : null

    const buildLines = [
      colorLabel ? `Body: ${colorLabel}` : null,
      screenTypeLabel ? `Screen Type: ${screenTypeLabel}` : null,
      screenColorLabel ? `Screen Color: ${screenColorLabel}` : null,
      fabricColorLabel ? `Fabric: ${fabricColorLabel}` : null,
      bottomFabricLabel ? `Bottom Fabric: ${bottomFabricLabel}` : null,
      bottomScreenLabel ? `Bottom Screen: ${bottomScreenLabel}` : null,
      micLabel ? `Mic Type: ${micLabel}` : null,
      popLabel ? `Pop Filter: ${popLabel}` : null
    ]
      .filter(Boolean)
      .join('\n')
    const shareText = [
      'Check out my custom Ohma mic build:',
      shareUrl,
      '',
      micLabel ? `Microphone Type: ${micLabel}` : null,
      colorLabel ? `Body Color: ${colorLabel}` : null,
      screenTypeLabel ? `Screen Type: ${screenTypeLabel}` : null,
      screenColorLabel ? `Screen Color: ${screenColorLabel}` : null,
      fabricColorLabel ? `Fabric Color: ${fabricColorLabel}` : null,
      bottomFabricLabel ? `Bottom Fabric Color: ${bottomFabricLabel}` : null,
      bottomScreenLabel ? `Bottom Screen Type: ${bottomScreenLabel}` : null,
      popLabel ? `Pop Filter: ${popLabel}` : null
    ]
      .filter((l) => l !== null)
      .join('\n')

    // Try sharing with the thumbnail image if available (prioritize share_thumbnail_url)
    const thumbnailUrl = design.share_thumbnail_url || design.thumbnail_url
    if (thumbnailUrl && navigator.share && navigator.canShare) {
      try {
        const response = await fetch(thumbnailUrl)
        const blob = await response.blob()
        const file = new File([blob], `my-ohma-mic-build-${fileid}.png`, { type: 'image/png' })
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ text: shareText, files: [file] })
          return
        }
      } catch (e) {
        if (e.name === 'AbortError') return
        // Fall through to URL-only share
      }
    }

    // Try URL-only share (no image)
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url: shareUrl })
        return
      } catch (e) {
        if (e.name === 'AbortError') return
      }
    }

    // Fallback: copy full text + link to clipboard
    try {
      await navigator.clipboard.writeText(shareText)
      setDesigns((prev) => prev.map((d) => (d.id === design.id ? { ...d, _linkCopied: true } : d)))
      setTimeout(() => setDesigns((prev) => prev.map((d) => (d.id === design.id ? { ...d, _linkCopied: false } : d))), 2000)
    } catch (e) {}
  }
  const doDuplicate = async (design) => {
    if (designs.length >= 24) return 'limit'
    try {
      // Prioritize high-res share_thumbnail_url if available
      const thumbnailUrl = design.share_thumbnail_url || design.thumbnail_url
      const data = await apiFetch('/api/designs', {
        method: 'POST',
        body: JSON.stringify({
          name: `Copy of ${design.name || 'Untitled Build'}`,
          config: design.config,
          thumbnail_url: thumbnailUrl,
          share_thumbnail_url: design.share_thumbnail_url || thumbnailUrl
        })
      })
      if (data.design) setDesigns((prev) => [data.design, ...prev])
      setDetailDesign(null)
    } catch (e) {
      console.error('Duplicate failed:', e)
    }
  }
  const initiateLoad = (design) => {
    setDetailDesign(null)
    // Parse config if Postgres returned it as a JSON string
    const cfg =
      typeof design.config === 'string'
        ? (() => {
            try {
              return JSON.parse(design.config)
            } catch {
              return design.config
            }
          })()
        : design.config
    setConfirmLoad({ ...design, config: cfg })
  }
  const execLoad = () => {
    if (!confirmLoad || !onLoadDesign) return
    onLoadDesign(confirmLoad.config, confirmLoad.id, confirmLoad.name)
    setConfirmLoad(null)
  }
  const saveAndLoad = async () => {
    builderBus.setLoading?.('Saving Build...')
    await saveCurrent()
    builderBus.setLoading?.(null)
    execLoad()
  }
  const logout = () => {
    window.SHOPIFY_CUSTOMER_TOKEN = null
    try {
      localStorage.removeItem('ohma_ctoken')
      localStorage.removeItem('ohma_ctoken_exp')
    } catch (e) {}
    setLoggedIn(false)
    setDesigns([])
  }

  const doAddToCart = async (design) => {
    const cfg = design.config || {}
    const priceUSD = cfg.totalPriceUSD
    const variants = window.productJson?.variants || []
    if (!variants.length) {
      alert('Product info not available. Please try from the configurator.')
      return
    }
    const cp = cfg.cartProperties || {}
    // Find variant: SKU first, then price, then first variant
    let match = cp.SKU ? variants.find((v) => v.sku === cp.SKU) : null
    if (!match && priceUSD) {
      const targetCents = priceUSD * 100
      // the regular price: during a sale (Black Friday) the variant's compare-at price
      const full = (v) => (v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price)
      match = variants.find((v) => full(v) === targetCents)
      if (!match) {
        const closest = variants.reduce((c, v) => (!c || Math.abs(full(v) - targetCents) < Math.abs(full(c) - targetCents) ? v : c), null)
        if (closest && Math.abs(full(closest) - targetCents) <= 20000) match = closest
      }
    }
    if (!match) match = variants[0]
    const fileid = Date.now()
    builderBus.setLoading?.('Adding to Cart...')
    // Build cart properties — use saved cartProperties if available, otherwise derive from config
    const hasCp = Object.keys(cp).length > 0
    const properties = {
      'Build Name': cp['Build Name'] || design.name || 'My Build',
      'Body Color': cp['Body Color'] || colorOptions[cfg.color] || cfg.color || '',
      'Screen Type': cp['Screen Type'] || cfg.screenType || '',
      'Screen Color': cp['Screen Color'] || screenColorOptions[cfg.screenColor] || cfg.screenColor || '',
      'Fabric Color': cp['Fabric Color'] || fabricColorOptions[cfg.fabricColor] || cfg.fabricColor || '',
      'Bottom Fabric Color': cp['Bottom Fabric Color'] || bottomfabricColorOptions[cfg.bottomFabricColor] || cfg.bottomFabricColor || '',
      'Bottom Screen Type': cp['Bottom Screen Type'] || cfg.bottomScreenType || '',
      'Microphone Type': cp['Microphone Type'] || cfg.microphoneType || '',
      ...((hasCp ? cp['Pop Filter'] : cfg.popFilterEnabled)
        ? {
            'Pop Filter': 'Yes',
            'Pop Filter Color':
              cp['Pop Filter Color'] ||
              (cfg.popFilterMaterial === 'plastic'
                ? 'Black (Plastic)'
                : (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((cfg.popFilterColor || '').toLowerCase())
                    ? 'Black'
                    : colorOptions[cfg.popFilterColor] || cfg.popFilterColor || '') + ' (Metal)')
          }
        : {}),
      ...((hasCp ? cp['Shock Mount'] : cfg.shockMountEnabled)
        ? { 'Shock Mount': 'Yes', 'Shock Mount Color': cp['Shock Mount Color'] || colorOptions[cfg.shockMountColor] || cfg.shockMountColor || '' }
        : {}),
      ...((hasCp ? cp['Pad'] : cfg.padEnabled && cfg.microphoneType !== 'Ribbon') ? { Pad: 'Add Pad' } : {}),
      _Pic: '', // underscored: hidden from customers, still on the order and in ShipStation
      _fileid: fileid,
      SKU: cp.SKU || ''
    }

    // Set Pic — use existing Cloudflare URL only (not base64)
    const isRealUrl = (v) => v && typeof v === 'string' && v.startsWith('http') && !v.startsWith('data:')
    let picUrl = isRealUrl(design.thumbnail_url) ? design.thumbnail_url : isRealUrl(design.share_thumbnail_url) ? design.share_thumbnail_url : ''
    if (!picUrl && design.thumbnail_base64) {
      try {
        const blob = await fetch(design.thumbnail_base64).then((r) => r.blob())
        // Try to resize to 360×360 — fall back to original blob if createImageBitmap fails
        let uploadBlob = blob
        try {
          const bmp = await createImageBitmap(blob)
          const canvas = document.createElement('canvas')
          canvas.width = 360
          canvas.height = 360
          const ctx = canvas.getContext('2d')
          const size = Math.min(bmp.width, bmp.height)
          const sx = (bmp.width - size) / 2
          const sy = (bmp.height - size) / 2
          ctx.drawImage(bmp, sx, sy, size, size, 0, 0, 360, 360)
          bmp.close()
          uploadBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9))
        } catch {}
        picUrl = await uploadToCloudflare(uploadBlob, `${fileid}_thumb.jpg`)
      } catch (e) {
        console.warn('[doAddToCart] CF upload failed:', e.message)
      }
    }
    properties._Pic = picUrl

    try {
      // Register config with Heroku — MUST complete before cart submit so ?id= load works
      const token = window.SHOPIFY_CUSTOMER_TOKEN || localStorage.getItem('ohma_ctoken') || ''
      try {
        await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
          body: JSON.stringify({
            fileid,
            name: design.name || 'My Build',
            email: '',
            color: cfg.color,
            colorName: colorOptions[cfg.color] ?? cfg.color ?? null,
            screenType: cfg.screenType,
            screenColor: cfg.screenColor,
            fabricColor: cfg.fabricColor,
            bottomFabricColor: cfg.bottomFabricColor,
            bottomScreenType: cfg.bottomScreenType,
            microphoneType: cfg.microphoneType,
            popFilterEnabled: cfg.popFilterEnabled,
            popFilterColor: cfg.popFilterColor,
            shockMountEnabled: cfg.shockMountEnabled,
            shockMountColor: cfg.shockMountColor,
            padEnabled: cfg.padEnabled,
            Pic: picUrl,
            currentDesignId: design.id
          })
        })
      } catch {} // swallow Heroku errors — don't block cart add
      if (!match?.id) throw new Error('No valid variant found')
      // Use form POST to add to cart — works both same-origin and cross-origin
      // and avoids CORS issues from customize.ohmaworld.com
      const cartBase = window.location.hostname.includes('customize.') ? 'https://ohmaworld.com' : ''
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${cartBase}/cart/add`
      const addInput = (name, value) => {
        const el = document.createElement('input')
        el.type = 'hidden'
        el.name = name
        el.value = value
        form.appendChild(el)
      }
      addInput('id', match.id)
      addInput('quantity', 1)
      addInput('return_to', '/checkout') // the button says Continue to Checkout
      Object.entries(properties).forEach(([k, v]) => {
        if (!v) return
        const str = String(v)
        if (str.startsWith('data:')) return
        addInput(`properties[${k}]`, str.slice(0, 255))
      })
      document.body.appendChild(form)
      track('added_to_cart', { from: 'my_builds', mic_type: properties['Microphone Type'] || null, body_color: properties['Body Color'] || null, screen_type: properties['Screen Type'] || null, screen_color: properties['Screen Color'] || null }, { beacon: true })
      form.submit()
    } catch (e) {
      builderBus.setLoading?.(null)
      alert('Something went wrong: ' + e.message)
    }
  }

  // Confirm modal — shown before doAddToCart executes
  const DesignConfirmModal = pendingAddToCart ? (
    <div
      onClick={() => setPendingAddToCart(null)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 480,
          width: '100%',
          padding: '28px 28px 24px',
          boxShadow: 'none'
        }}>
        <h2 style={{ fontFamily: FONT_HEADER, fontWeight: 500, fontSize: '1.1em', letterSpacing: '0.04em', marginTop: 0, marginBottom: 16 }}>Final Step</h2>
        <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#444', lineHeight: 1.7, marginBottom: 20 }}>
          You've built your microphone. Once you place your order, we begin making it in-house in East Los Angeles over about 4–6 weeks. This is a digital
          preview — in person, the microphone has depth and texture that can't be fully captured on screen. Once we begin, the build is locked. Questions?{' '}
          <a href="mailto:info@ohmaworld.com" style={{ color: '#555' }}>
            info@ohmaworld.com
          </a>
          .
        </p>
        {pendingAddToCart?.config?.bottomScreenType === 'Custom Logo' && (
          <p style={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 20 }}>
            If your build includes a custom logo, send artwork to{' '}
            <a href="mailto:sammy@ohmaworld.com" style={{ color: '#555' }}>
              sammy@ohmaworld.com
            </a>
            .
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setPendingAddToCart(null)}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: 10,
              background: '#fff',
              fontFamily: FONT_BODY,
              fontSize: 14,
              cursor: 'pointer',
              color: '#555'
            }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              const d = pendingAddToCart
              setPendingAddToCart(null)
              await doAddToCart(d)
            }}
            style={{
              padding: '10px 24px',
              border: 'none',
              borderRadius: 10,
              background: '#6186f7',
              color: '#fff',
              fontFamily: FONT_HEADER,
              fontWeight: 700,
              fontSize: '0.65em',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer'
            }}>
            Continue to Checkout
          </button>
        </div>
      </div>
    </div>
  ) : null

  const sorted = React.useMemo(() => {
    let result = [...designs]

    // Apply filters
    if (favOnly || filters.favoritesOnly) {
      result = result.filter((d) => d.is_favorite)
    }

    // Filter by mic type
    if (filters.micTypes.length > 0) {
      result = result.filter((d) => {
        const micType = d.config?.microphoneType
        return filters.micTypes.includes(micType)
      })
    }

    // Filter by screen type
    if (filters.screenTypes.length > 0) {
      result = result.filter((d) => {
        const screenType = d.config?.screenType
        return filters.screenTypes.includes(screenType)
      })
    }

    // Then apply sort
    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        break
      case 'az':
        result.sort((a, b) => (a.name || 'Untitled').localeCompare(b.name || 'Untitled'))
        break
      case 'favorites':
        result.sort((a, b) => {
          if (a.is_favorite === b.is_favorite) {
            return new Date(b.created_at) - new Date(a.created_at) // newest first within same fav status
          }
          return a.is_favorite ? -1 : 1 // favorites first
        })
        break
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        break
    }

    return result
  }, [designs, favOnly, sortBy, filters])

  // Local builds helpers
  const LOCAL_BUILDS_KEY = 'ohma_local_builds'
  const LOCAL_BUILDS_LIMIT = 2
  const getLocalBuilds = () => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
    } catch (e) {
      return []
    }
  }

  const [localBuilds, setLocalBuilds] = React.useState(() => getLocalBuilds())
  const [showDuplicateLimitToast, setShowDuplicateLimitToast] = React.useState(false)
  const [pulseLoggedIn, setPulseLoggedIn] = React.useState(false)
  const atLimit = localBuilds.length >= LOCAL_BUILDS_LIMIT
  const [pulseSignIn, setPulseSignIn] = React.useState(false)

  // Refresh local builds when __ohmaRefreshBuilds fires
  React.useEffect(() => {
    const prev = builderBus.refreshBuilds
    builderBus.refreshBuilds = () => {
      setLocalBuilds(getLocalBuilds())
      prev?.()
    }
    return () => {
      builderBus.refreshBuilds = prev
    }
  }, [])

  const handleLoginSuccess = async () => {
    // Prevent double migration (can be called from form submit AND useEffect)
    if (handleLoginSuccess._running) return
    handleLoginSuccess._running = true
    setLoginLoading(true)
    // Migrate local builds to account on login
    const token = window.SHOPIFY_CUSTOMER_TOKEN || localStorage.getItem('ohma_ctoken') || ''
    const locals = getLocalBuilds()
    const migratedShareUrls = [] // collect { id, share_thumbnail_url } pairs for post-fetch patch
    if (locals.length > 0 && token) {
      for (const local of locals) {
        try {
          // Upload square thumbnail
          let thumbnailUrl = null
          if (local.thumbnail_base64) {
            try {
              const dataUrl = local.thumbnail_base64
              const base64 = dataUrl.split(',')[1]
              const mime = dataUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg'
              const binary = atob(base64)
              const bytes = new Uint8Array(binary.length)
              for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
              const blob = new Blob([bytes], { type: mime })
              thumbnailUrl = (await uploadToCloudflare(blob, `build_${Date.now()}.jpg`)) || null
            } catch (e) {}
          }
          // Upload portrait share screenshot if exists
          let shareThumbnailUrl = null
          if (local.share_thumbnail_base64) {
            try {
              // Convert base64 data URL to blob without fetch() — more reliable on mobile
              const dataUrl = local.share_thumbnail_base64
              const base64 = dataUrl.split(',')[1]
              const mime = dataUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg'
              const binary = atob(base64)
              const bytes = new Uint8Array(binary.length)
              for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
              const blob = new Blob([bytes], { type: mime })
              shareThumbnailUrl = (await uploadToCloudflare(blob, `share_${Date.now()}.jpg`)) || null
            } catch (e) {}
          }
          // Use fileid from local config, or generate one
          const migrateFileid = local.config?.fileid || Date.now()
          const migrateConfig = { ...local.config, fileid: migrateFileid }
          // POST to Heroku so the ?id= URL loads the build without auth
          fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
            body: JSON.stringify({ ...migrateConfig, fileid: migrateFileid })
          }).catch(() => {})
          const postRes = await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
            body: JSON.stringify({ name: local.name, config: migrateConfig, thumbnail_url: thumbnailUrl })
          })
          try {
            const postData = await postRes.json()
            const newId = postData?.design?.id
            if (newId) {
              // PUT share_thumbnail_url separately — same pattern as handleSaveDesign
              // The POST handler may not accept share_thumbnail_url
              if (shareThumbnailUrl) {
                await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${newId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
                  body: JSON.stringify({ share_thumbnail_url: shareThumbnailUrl })
                }).catch(() => {})
              }
              migratedShareUrls.push({
                id: newId,
                fileid: migrateFileid,
                share_thumbnail_url: shareThumbnailUrl,
                thumbnail_url: thumbnailUrl
              })
            }
          } catch (e) {}
        } catch (e) {}
      }
      localStorage.removeItem(LOCAL_BUILDS_KEY)
      setLocalBuilds([])
    }
    setLoggedIn(true)
    setLoginLoading(false)
    handleLoginSuccess._running = false
    // Fetch after migration then patch URLs we know about
    await new Promise((r) => setTimeout(r, 800))
    await fetchDesigns()
    if (migratedShareUrls.length > 0) {
      // Store in persistent ref so subsequent fetchDesigns calls preserve these
      migratedShareUrls.forEach(({ id, share_thumbnail_url, thumbnail_url }) => {
        if (id && share_thumbnail_url) _knownShareUrls[id] = share_thumbnail_url
      })
      setDesigns((prev) =>
        prev.map((d) => {
          const match = migratedShareUrls.find((m) => m.id === d.id)
          if (!match) return d
          return {
            ...d,
            ...(match.share_thumbnail_url ? { share_thumbnail_url: match.share_thumbnail_url } : {}),
            ...(match.thumbnail_url ? { thumbnail_url: match.thumbnail_url } : {}),
            ...(match.fileid ? { config: { ...(d.config || {}), fileid: match.fileid } } : {})
          }
        })
      )
      // Notify App.js of the new server ID — if only 1 build was migrated, it's the active one
      if (migratedShareUrls.length === 1 && onMigratedId) {
        onMigratedId(migratedShareUrls[0].id)
      }
    }
    // Second fetch after a delay catches any stragglers
    setTimeout(() => fetchDesigns(), 3000)
  }

  if (loginLoading)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 16 }}>
        <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}} .ohma-spinner{animation:ohma-spin 0.9s linear infinite}`}</style>
        <svg className="ohma-spinner" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#91c3de" strokeWidth="2.5" strokeLinecap="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
        <span style={{ fontFamily: "'Jost', sans-serif", fontSize: 14, color: '#808082' }}>Logging in…</span>
      </div>
    )

  // Early-return local detail view — renders directly in pm-body
  if (detailDesign?.source === 'local') {
    return (
      <>
        {DesignConfirmModal}
        <LocalDetailSheet
          build={detailDesign}
          screenshotUrl={detailDesign.share_thumbnail_url || detailDesign.share_thumbnail_base64 || null}
          onClose={() => setDetailDesign(null)}
          onLoad={onLoadDesign}
          onDelete={(id) => {
            try {
              const builds = JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
              const updated = builds.filter((b) => b.id !== id)
              localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(updated))
              setLocalBuilds(updated)
            } catch (e) {}
            setDetailDesign(null)
          }}
          onRename={(id, newName) => {
            try {
              const builds = JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
              const updated = builds.map((b) => (b.id === id ? { ...b, name: newName } : b))
              localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(updated))
              setLocalBuilds(updated)
              setDetailDesign((prev) => (prev ? { ...prev, name: newName } : prev))
            } catch (e) {}
          }}
          onDuplicate={(build) => {
            try {
              const builds = JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
              if (builds.length >= LOCAL_BUILDS_LIMIT) {
                return 'limit'
              }
              const dupe = { ...build, id: 'local_' + Date.now(), name: 'Copy of ' + build.name, created_at: new Date().toISOString() }
              builds.unshift(dupe)
              localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(builds))
              setLocalBuilds([...builds])
            } catch (e) {}
            setDetailDesign(null)
          }}
          onShare={(build) => doLocalShare(build)}
          FONT_HEADER={FONT_HEADER}
          FONT_BODY={FONT_BODY}
          COLOR_MUTED={COLOR_MUTED}
        />
      </>
    )
  }

  if (!loggedIn)
    return (
      <div
        className="sd-tab-wrapper"
        style={{
          padding: 'clamp(16px, 4vw, 40px)',
          paddingLeft: '20px',
          paddingRight: '20px',
          position: 'relative',
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          minHeight: 0
        }}>
        <div style={{ maxWidth: 420, margin: '0 auto' }}>
          <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}} @keyframes ohma-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}} .ohma-spinner{animation:ohma-spin 0.9s linear infinite} .sd-mobile-header{margin-bottom:12px !important} @media (orientation:landscape) and (min-width:769px){.sd-mobile-header{display:none}}`}</style>

          {/* Auth first — visible immediately */}
          <h3 className="sd-mobile-header" style={{ margin: '0 0 6px', fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>
            My Builds
          </h3>
          <p style={{ margin: '0 0 16px', fontFamily: FONT_BODY, fontSize: 14, color: COLOR_MUTED, lineHeight: 1.6 }}>
            Save this build and more by signing in.
          </p>
          <div id="sd-auth-form">
            <AuthForm onSuccess={handleLoginSuccess} />
          </div>

          {/* Local builds below auth */}
          {localBuilds.length > 0 && (
            <div style={{ marginTop: 28, paddingTop: 24, borderTop: `1px solid #e8e8e6` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontFamily: FONT_HEADER, fontWeight: 700, fontSize: 13, color: '#333', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Your Saved Builds
                </span>
                <span style={{ fontFamily: FONT_BODY, fontSize: 12, color: atLimit ? '#e63946' : COLOR_MUTED }}>
                  {localBuilds.length}/{24}
                </span>
              </div>
              {atLimit && (
                <div
                  style={{
                    background: '#fff8ee',
                    border: '1px solid #f0c97a',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 12,
                    animation: pulseSignIn ? 'ohma-limit-pulse 0.5s ease-in-out 2' : 'none'
                  }}>
                  <style>{`@keyframes ohma-limit-pulse { 0%,100%{background:#fff8ee} 50%{background:#fce8b2} }`}</style>
                  <p style={{ margin: 0, fontFamily: FONT_BODY, fontSize: 13, color: '#7a5a00', lineHeight: 1.5 }}>
                    Save this build and more by{' '}
                    <span
                      onClick={() => {
                        const el = document.getElementById('sd-auth-form')
                        if (el) el.scrollIntoView({ behavior: 'smooth' })
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                      style={{ textDecoration: 'underline', cursor: 'pointer', color: '#7a5a00' }}>
                      signing in
                    </span>
                    .
                  </p>
                </div>
              )}
              {showDuplicateLimitToast && (
                <div
                  style={{
                    background: '#000',
                    color: '#fff',
                    fontSize: '12px',
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    marginBottom: 12,
                    lineHeight: '1.3'
                  }}>
                  Save this build and more by{' '}
                  <span
                    onClick={() => {
                      const el = document.getElementById('sd-auth-form')
                      if (el) el.scrollIntoView({ behavior: 'smooth' })
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    signing in
                  </span>
                  .
                </div>
              )}
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, paddingLeft: 4, paddingRight: 4 }}
                className="sd-local-builds-grid">
                <style>{`
                  .sd-local-builds-grid { 
                    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important; 
                    gap: 8px !important; 
                  }
                  
                  @media (orientation: landscape) and (min-width: 769px) { 
                    .sd-local-builds-grid { 
                      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important; 
                      gap: 16px !important; 
                    }
                  }
                `}</style>
                {localBuilds.map((build) => {
                  const cardDesign = {
                    ...build,
                    share_thumbnail_url: build.share_thumbnail_base64 || null,
                    thumbnail_url: build.thumbnail_base64 || null,
                    is_favorite: false,
                    source: 'local'
                  }
                  return (
                    <DesignCard
                      key={build.id}
                      design={cardDesign}
                      isActive={currentDesignId === build.id}
                      isLoggedIn={false}
                      onAtLimit={() => {
                        setPulseSignIn(true)
                        setTimeout(() => setPulseSignIn(false), 1200)
                      }}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      onInfo={() => setDetailDesign(cardDesign)}
                      onLoad={() => {
                        if (onLoadDesign) onLoadDesign(build.config)
                      }}
                      onDelete={() => {
                        try {
                          const builds = JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
                          const updated = builds.filter((b) => b.id !== build.id)
                          localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(updated))
                          setLocalBuilds(updated)
                        } catch (e) {}
                      }}
                      onDuplicate={() => {
                        try {
                          const builds = JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
                          if (builds.length >= LOCAL_BUILDS_LIMIT) {
                            return 'limit'
                          }
                          const dupe = { ...build, id: `local_${Date.now()}`, name: `Copy of ${build.name}`, created_at: new Date().toISOString() }
                          const updated = [dupe, ...builds]
                          localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(updated))
                          setLocalBuilds(updated)
                        } catch (e) {}
                      }}
                      onFavorite={null}
                      onShare={(b) => doLocalShare(b)}
                      onRename={(design) => {
                        setRenameModalDesign(design)
                        setRenameInputValue(design.name || '')
                      }}
                      onAddToCart={(design) => {
                        if (typeof builderBus.openConfirmForDesign === 'function') {
                          builderBus.openConfirmForDesign(design)
                        } else {
                          doAddToCart(design)
                        }
                      }}
                    />
                  )
                })}
              </div>
            </div>
          )}
          {DesignConfirmModal}
          {/* Rename Modal */}
          {renameModalDesign && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 5000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.4)',
                animation: 'fadeIn 200ms ease'
              }}
              onClick={() => {
                setRenameModalDesign(null)
                setRenameInputValue('')
              }}>
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: '24px',
                  maxWidth: '400px',
                  width: '90%',
                  boxShadow: 'none'
                }}>
                <div
                  style={{
                    fontFamily: FONT_HEADER,
                    fontWeight: 700,
                    fontSize: '0.9em',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#222',
                    marginBottom: 16
                  }}>
                  Rename Build
                </div>
                <input
                  type="text"
                  value={renameInputValue}
                  onChange={(e) => setRenameInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') executeRename()
                    if (e.key === 'Escape') {
                      setRenameModalDesign(null)
                      setRenameInputValue('')
                    }
                  }}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '15px',
                    fontFamily: FONT_BODY,
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                    outline: 'none',
                    marginBottom: 20,
                    transition: 'border-color 180ms'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#e98969'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0'
                  }}
                />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setRenameModalDesign(null)
                      setRenameInputValue('')
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#f5f5f5',
                      color: '#555',
                      border: 'none',
                      borderRadius: 999,
                      cursor: 'pointer',
                      fontFamily: FONT_HEADER,
                      fontWeight: 700,
                      fontSize: '0.65em',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      transition: 'background 180ms'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e8e8e8'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f5f5f5'
                    }}>
                    Cancel
                  </button>
                  <button
                    onClick={executeRename}
                    style={{
                      padding: '10px 20px',
                      background: '#e98969',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      cursor: 'pointer',
                      fontFamily: FONT_HEADER,
                      fontWeight: 700,
                      fontSize: '0.65em',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      transition: 'background 180ms'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#d67857'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#e98969'
                    }}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )

  // Early-return detail view — renders directly in pm-body which has real pixel height
  if (detailDesign) {
    return (
      <DetailSheet
        design={detailDesign}
        onLoad={initiateLoad}
        onDelete={doDelete}
        onDuplicate={(design) => {
          if (designs.length >= 24) return 'limit'
          doDuplicate(design)
        }}
        onFavorite={doFavorite}
        onRename={doRename}
        onAddToCart={(design) => {
          if (typeof builderBus.openConfirmForDesign === 'function') {
            builderBus.openConfirmForDesign(design)
          } else {
            doAddToCart(design)
          }
        }}
        onClose={() => setDetailDesign(null)}
        screenshotUrl={detailDesign.share_thumbnail_url || null}
        currentDesignId={currentDesignId}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    )
  }

  return (
    <div
      className="sd-tab-wrapper"
      onClick={(e) => {
        // Close all menus when clicking on backdrop (not on menu content)
        if (e.target === e.currentTarget || e.target.classList.contains('sd-tab-wrapper')) {
          setOpenMenuId(null)
          setShowFilterMenu(false)
          setShowSortMenu(false)
        }
      }}
      style={{
        padding: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
        minHeight: 0
      }}>
      <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}} .ohma-spinner{animation:ohma-spin 0.9s linear infinite} .sd-mobile-header{margin-bottom:12px !important} .sd-header-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px} .sd-desktop-logout{display:none !important} .sd-desktop-topbar{display:none !important} @media (orientation:landscape) and (min-width:769px){.sd-mobile-header{display:none !important} .sd-header-row{display:none !important} .sd-desktop-logout{display:flex !important} .sd-desktop-topbar{display:block !important}}`}</style>
      {/* Header — mobile only, hidden on desktop by sd-header-row CSS */}
      <div className="sd-header-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>My Builds</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: '#e98969',
                fontWeight: 500,
                display: 'inline-block',
                animation: pulseLoggedIn ? 'ohma-limit-pulse 0.5s ease-in-out 2' : 'none'
              }}>
              <style>{`@keyframes ohma-limit-pulse { 0%,100%{color:#e98969} 50%{color:#e63946} }`}</style>
              {designs.length} / 24
            </span>
            <HoverBtn onClick={logout} className="sd-header-btn" style={{}}>
              Log out
            </HoverBtn>
          </div>
        </div>
        {/* Mobile TopBar */}
        <TopBar
          favOnly={favOnly}
          onToggleFav={() => setFavOnly((v) => !v)}
          onLogout={logout}
          sortBy={sortBy}
          onSortChange={setSortBy}
          filters={filters}
          onFiltersChange={setFilters}
          showSortMenu={showSortMenu}
          setShowSortMenu={setShowSortMenu}
          showFilterMenu={showFilterMenu}
          setShowFilterMenu={setShowFilterMenu}
        />
      </div>

      {/* Desktop top bar only — mobile uses sd-header-row above */}
      <div className="sd-desktop-topbar">
        <TopBar
          favOnly={favOnly}
          onToggleFav={() => setFavOnly((v) => !v)}
          onLogout={logout}
          sortBy={sortBy}
          onSortChange={setSortBy}
          filters={filters}
          onFiltersChange={setFilters}
          showSortMenu={showSortMenu}
          setShowSortMenu={setShowSortMenu}
          showFilterMenu={showFilterMenu}
          setShowFilterMenu={setShowFilterMenu}
          buildCount={designs.length}
        />
      </div>

      {showSaveForm && <SavePrompt onSave={saveNew} onCancel={() => setShowSaveForm(false)} />}
      {error && <p style={{ color: '#e63946', fontFamily: FONT_BODY, fontSize: 13, margin: '0 0 12px' }}>{error}</p>}

      {loading && designs.length === 0 ? (
        <Spinner />
      ) : sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#bbb', fontFamily: FONT_BODY, fontSize: 13, lineHeight: 1.8 }}>
          {favOnly ? 'No favorites yet.\nStar a build to find it here.' : 'No saved builds yet.\nHit "+ New Save" to save your first build.'}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 8,
            paddingBottom: 16,
            paddingLeft: 4,
            paddingRight: 4,
            marginBottom: 24
          }}
          className="sd-builds-grid">
          <style>{`
            .sd-builds-grid { 
              grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important; 
              gap: 8px !important; 
            }
            
            @media (orientation: landscape) and (min-width: 769px) { 
              .sd-builds-grid { 
                grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important; 
                gap: 16px !important; 
              }
            }
          `}</style>
          {sorted.map((d) => {
            const isActive = currentDesignId && d.id === currentDesignId && !hasUnsavedChanges
            if (d.id === currentDesignId) {
            }
            return (
              <div key={d.id} style={{ position: 'relative' }}>
                <DesignCard
                  design={d}
                  isActive={isActive}
                  isLoggedIn={true}
                  onInfo={setDetailDesign}
                  onLoad={initiateLoad}
                  onDelete={() => setConfirmDeleteId(d.id)}
                  onDuplicate={doDuplicate}
                  onFavorite={doFavorite}
                  onShare={doShare}
                  onRename={(design) => {
                    setRenameModalDesign(design)
                    setRenameInputValue(design.name || '')
                  }}
                  onAddToCart={(design) => {
                    if (typeof builderBus.openConfirmForDesign === 'function') {
                      builderBus.openConfirmForDesign(design)
                    } else {
                      doAddToCart(design)
                    }
                  }}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                />
              </div>
            )
          })}
        </div>
      )}

      {DesignConfirmModal}
      {confirmLoad && (
        <LoadConfirm
          design={confirmLoad}
          hasUnsaved={hasUnsavedChanges}
          currentDesignName={currentDesignName}
          onSaveAndLoad={saveAndLoad}
          onDiscard={execLoad}
          onCancel={() => setConfirmLoad(null)}
        />
      )}

      {/* Rename Modal */}
      {/* Rename Modal */}
      {renameModalDesign && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            animation: 'fadeIn 200ms ease'
          }}
          onClick={() => {
            setRenameModalDesign(null)
            setRenameInputValue('')
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: 'none'
            }}>
            <div
              style={{
                fontFamily: FONT_HEADER,
                fontWeight: 700,
                fontSize: '0.9em',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#222',
                marginBottom: 16
              }}>
              Rename Build
            </div>
            <input
              type="text"
              value={renameInputValue}
              onChange={(e) => setRenameInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') executeRename()
                if (e.key === 'Escape') {
                  setRenameModalDesign(null)
                  setRenameInputValue('')
                }
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '15px',
                fontFamily: FONT_BODY,
                border: '2px solid #e0e0e0',
                borderRadius: 8,
                outline: 'none',
                marginBottom: 20,
                transition: 'border-color 180ms'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#e98969'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0'
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setRenameModalDesign(null)
                  setRenameInputValue('')
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  color: '#555',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: FONT_HEADER,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'background 180ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e8e8e8'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f5f5f5'
                }}>
                Cancel
              </button>
              <button
                onClick={executeRename}
                style={{
                  padding: '10px 20px',
                  background: '#e98969',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: FONT_HEADER,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'background 180ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d67857'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e98969'
                }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            animation: 'fadeIn 200ms ease'
          }}
          onClick={() => setConfirmDeleteId(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: 'none'
            }}>
            <div
              style={{
                fontFamily: FONT_HEADER,
                fontWeight: 700,
                fontSize: '0.9em',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#222',
                marginBottom: 8
              }}>
              Delete This Build?
            </div>
            <div style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#808082', marginBottom: 20 }}>This can't be undone.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  color: '#555',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: FONT_HEADER,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'background 180ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e8e8e8'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f5f5f5'
                }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const design = designs.find((d) => d.id === confirmDeleteId)
                  if (design) executeDelete(design)
                }}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  background: '#e63946',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: FONT_HEADER,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'background 180ms',
                  opacity: deleting ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!deleting) e.currentTarget.style.background = '#c0303b'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e63946'
                }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
